import BavContributionTable from '../components/charts/BavContributionTable';
import { chartCalculationHeaders } from '../utils/chartCalculationTransport';
import { chartAyanamshaSettings } from '../utils/customAyanamsha';
import { adaptiveInk } from '../theme/appearance';
import { useDashaCycles } from '../hooks/useDashaCycles';
import DashaCycleSelector from '../components/dasha/DashaCycleSelector';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AnalysisTabBar from '../components/AnalysisTabBar';
import {
  useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  List,
  ListItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tabs,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { LocalizedButton as Button, LocalizedChip as Chip, LocalizedFormControlLabel as FormControlLabel, LocalizedInputLabel as InputLabel, LocalizedListItemText as ListItemText, LocalizedMenuItem as MenuItem, LocalizedTab as Tab, LocalizedTableCell as TableCell } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import { useChart } from '../contexts/ChartContext';
import { useAuth } from '../contexts/AuthContext';
import { hasPremiumSubscription } from '../utils/subscription';
import DivisionalChart from '../components/charts/DivisionalChart';
import BhavaChalitChart from '../components/charts/BhavaChalitChart';
import { EastIndianChartRenderer, SouthIndianChartRenderer } from '../components/charts/NorthIndianChart';
import SeoHead from '../components/seo/SeoHead';
import { calculateDashaSubPeriods, DashaEntry } from '../utils/dashaCalculator';
import { useTranslation } from 'react-i18next';
import { getPlanetAbbr, getSignAbbr } from '../i18n/astrology';
import { vedicNakshatraIndex, vedicNakshatraLord, vedicNakshatraName, vedicUpagrahaLabel } from '../i18n/vedicNakshatras';
import { translateInline } from '../i18n/inlineTranslations';
import { sadeSatiText } from '../i18n/sadeSati';
import VedicYogasPanel from '../components/vedic/VedicYogasPanel';
import { useVedicYogas } from '../hooks/useVedicYogas';
import PanchangAlmanac from '../components/panchang/PanchangAlmanac';
import { useCurrentTransitOverlay } from '../hooks/useCurrentTransitOverlay';
import { isOuterPlanetKey, useUserPreferences } from '../contexts/UserPreferencesContext';
import DoubleTransitPanel from '../components/transit/DoubleTransitPanel';
import SadeSatiPanel from '../components/vedic/SadeSatiPanel';
import DashaAnalysisPanel, { dashaAnalysisLabel } from '../components/dasha/DashaAnalysisPanel';
import FaladeshSystemPanel from '../components/faladesh/FaladeshSystemPanel';
import CollapsibleDashaTimeline from '../components/dasha/CollapsibleDashaTimeline';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';

interface DivisionalChartData {
  chartKey: string;
  chartName: string;
  purpose: string;
  lagna: { number: number; name: string; ruler: string };
  planets: {
    [key: string]: {
      name: string;
      sign: { number: number; name: string; ruler: string };
      longitude?: number;
      degree?: { formatted?: string; degrees?: number };
      nakshatra?: { name?: string; pada?: number } | string;
      pada?: number;
      isRetrograde: boolean;
      status: string[];
    };
  };
  upagrahas?: {
    [key: string]: {
      name: string;
      shortName?: string;
      longitude?: number;
      degreeInSign?: number;
      meaning?: string;
      sign: { number: number; name: string };
    };
  };
}

interface PanchangResult {
  date: string;
  day: string;
  tithi: string;
  nakshatra: string;
  yoga: string;
  karana: string;
  sunrise: string;
  sunset: string;
  details?: {
    tithiNumber?: number;
    nakshatraNumber?: number;
    nakshatraPada?: number;
    yogaNumber?: number;
    karanaNumber?: number;
    sunLongitude?: number;
    moonLongitude?: number;
  };
  [key: string]: any;
}

const STATUS_CHIP_STYLES: Record<string, { color: 'success' | 'error' | 'secondary' | 'warning' | 'default'; label: string }> = {
  E:  { color: 'success',   label: 'Exalted'      },
  Db: { color: 'error',     label: 'Debilitated'  },
  Mool: { color: 'secondary', label: 'Mooltrikona' },
  Swa: { color: 'default', label: 'Own Sign' },
  V:  { color: 'secondary', label: 'Vargottama'   },
  R:  { color: 'warning',   label: 'Retrograde'   },
  C:  { color: 'default',   label: 'Combust'      },
};

const PLANET_FULL: { [key: string]: string } = {
  SUN: 'Sun', MOON: 'Moon', MERCURY: 'Mercury', VENUS: 'Venus',
  MARS: 'Mars', JUPITER: 'Jupiter', SATURN: 'Saturn',
  RAHU: 'Rahu', KETU: 'Ketu',
};
const VEDIC_PLANET_ORDER = ['SUN', 'MOON', 'MARS', 'MERCURY', 'JUPITER', 'VENUS', 'SATURN', 'RAHU', 'KETU'];

const SIGNS = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces',
];

const getNakshatraPada = (value: any): number | string => {
  const direct = Number(value?.pada ?? value?.nakshatra?.pada);
  if (Number.isInteger(direct) && direct >= 1 && direct <= 4) return direct;
  const longitude = Number(value?.longitude);
  if (!Number.isFinite(longitude)) return '-';
  const nakshatraSize = 360 / 27;
  return Math.floor((((longitude % nakshatraSize) + nakshatraSize) % nakshatraSize) / (nakshatraSize / 4)) + 1;
};

const SIGN_LORDS: Record<string, string> = {
  Aries: 'Mars',
  Taurus: 'Venus',
  Gemini: 'Mercury',
  Cancer: 'Moon',
  Leo: 'Sun',
  Virgo: 'Mercury',
  Libra: 'Venus',
  Scorpio: 'Mars',
  Sagittarius: 'Jupiter',
  Capricorn: 'Saturn',
  Aquarius: 'Saturn',
  Pisces: 'Jupiter',
};

const PLANET_NAME_TO_KEY: Record<string, string> = {
  Sun: 'SUN',
  Moon: 'MOON',
  Mars: 'MARS',
  Mercury: 'MERCURY',
  Jupiter: 'JUPITER',
  Venus: 'VENUS',
  Saturn: 'SATURN',
  Rahu: 'RAHU',
  Ketu: 'KETU',
};

const NATURAL_NATURE_KEYS: Record<string, string> = {
  Sun: 'naturalMalefic',
  Moon: 'naturalBenefic',
  Mars: 'naturalMalefic',
  Mercury: 'conditionalBenefic',
  Jupiter: 'naturalBenefic',
  Venus: 'naturalBenefic',
  Saturn: 'naturalMalefic',
  Rahu: 'naturalMalefic',
  Ketu: 'naturalMalefic',
};

const EXALT_SIGN: Record<string, number> = { SUN:1, MOON:2, MARS:10, MERCURY:6, JUPITER:4, VENUS:12, SATURN:7, RAHU:3, KETU:9 };
const DEBILITATION_SIGN: Record<string, number> = { SUN:7, MOON:8, MARS:4, MERCURY:12, JUPITER:10, VENUS:6, SATURN:1, RAHU:9, KETU:3 };
const OWN_SIGNS: Record<string, number[]> = { SUN:[5], MOON:[4], MARS:[1,8], MERCURY:[3,6], JUPITER:[9,12], VENUS:[2,7], SATURN:[10,11] };
const MOOLTRIKONA_SIGN: Record<string, number> = { SUN:5, MOON:2, MARS:1, MERCURY:6, JUPITER:9, VENUS:7, SATURN:11 };
const RASHI_ELEMENTS = ['Agni (Fire)', 'Prithvi (Earth)', 'Vayu (Air)', 'Jal (Water)'] as const;
const RASHI_MODALITIES = ['Chara (Movable)', 'Sthira (Fixed)', 'Dwiswabhava (Dual)'] as const;

const getPlanetPlacementStatus = (planetKey: string, planet: any) => {
  const status = [...(planet?.status || [])];
  const signNumber = Number(planet?.sign?.number) || SIGNS.indexOf(String(planet?.sign?.name || planet?.sign || '')) + 1;
  if (MOOLTRIKONA_SIGN[planetKey] === signNumber && !status.includes('Mool')) status.push('Mool');
  if (OWN_SIGNS[planetKey]?.includes(signNumber) && !status.includes('Swa')) status.push('Swa');
  return status;
};

const DIVISIONAL_TAB_META = [
  {
    title: 'Charts',
    description: 'Review the natal Rashi, Chandra and Bhava Chalit charts.'
  },
  {
    title: 'Planet States',
    description: 'Review functional Shubh and Ashubh planets by Lagna with placement-based refinements.'
  },
  {
    title: 'Ashtakavarga',
    description: 'Inspect Ashtakavarga bindu scores and sign strengths for this chart.'
  },
  {
    title: 'Special Ascendants & Points',
    description: 'Review Bhava Lagna, Hora Lagna, Ghati Lagna, Sree Lagna, Bhrigu Bindu, Yogi, Dagdha Rashis and related Vedic points.'
  },
  {
    title: 'Shadbala',
    description: 'Open Shadbala strength analysis for planets in this chart.'
  },
  {
    title: 'Mahadasha',
    description: 'Review Vimshottari Mahadasha, Antardasha, and Pratyantardasha timelines.'
  },
  {
    title: 'Navtara',
    description: 'Explore Navtara relationships and nakshatra-based results for this chart.'
  },
  {
    title: 'Panchang',
    description: 'Review the Panchang at birth for this person and birthplace.'
  },
  {
    title: 'Yogas',
    description: 'Review confirmed, possible, and challenging Vedic yogas detected in this chart.'
  },
  {
    title: 'Double Transit',
    description: 'Find Guru–Shani double-transit activation windows for major life-event factors.'
  },
  {
    title: 'Natal + Transit Wheel',
    description: 'Review the natal chart with the current or selected sidereal transit overlay.'
  },
  {
    title: 'Sade Sati & Dhaiya',
    description: 'Review current and upcoming Sade Sati and fourth/eighth-house Dhaiya periods for the next 30 years.'
  },
];

const DIVISIONAL_CHART_ORDER = ['D1', 'D2', 'D3', 'D4', 'D6', 'D7', 'D8', 'D9', 'D10', 'D12', 'D16', 'D20', 'D24', 'D27', 'D28', 'D30', 'D40', 'D45', 'D50', 'D60'];

const dateToYmd = (value: any) => {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const timezoneToMinutes = (value: any) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    const match = trimmed.match(/^([+-]?)(\d{1,2})(?::?(\d{2}))?$/);
    if (match) {
      const sign = match[1] === '-' ? -1 : 1;
      return sign * ((Number(match[2]) || 0) * 60 + (Number(match[3]) || 0));
    }
    const named = trimmed.match(/UTC\s*([+-]\d{1,2})(?::?(\d{2}))?/i);
    if (named) {
      const hours = Number(named[1]) || 0;
      const minutes = Number(named[2]) || 0;
      return (hours < 0 ? -1 : 1) * (Math.abs(hours) * 60 + minutes);
    }
  }
  return 330;
};

const timeOfBirthToClock = (value: any) => {
  if (!value) return null;
  if (typeof value === 'string') {
    const match = value.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (!match) return null;
    return `${String(Number(match[1])).padStart(2, '0')}:${match[2]}:${match[3] || '00'}`;
  }
  const hour = Number(value.hour ?? value.hours ?? 0);
  const minute = Number(value.minute ?? value.minutes ?? 0);
  const second = Number(value.second ?? value.seconds ?? 0);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;
};

const previousYmd = (value: string) => {
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  date.setDate(date.getDate() - 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const isBirthBeforeSunrise = (chart: any, panchang: any) => {
  const date = dateToYmd(chart?.birthDetails?.dateOfBirth);
  const birthClock = timeOfBirthToClock(chart?.birthDetails?.timeOfBirth);
  const sunriseClock = String(panchang?.sunrise || '').slice(0, 5);
  if (!date || !birthClock || !/^\d{2}:\d{2}$/.test(sunriseClock)) return false;
  return new Date(`${date}T${birthClock}`).getTime() < new Date(`${date}T${sunriseClock}:00`).getTime();
};

const getChartPlanet = (chart: any, planetName: string) => {
  const planets = chart?.planets || {};
  const aliases = [planetName.toUpperCase(), planetName, planetName.toLowerCase()];
  for (const alias of aliases) {
    if (planets[alias]) return planets[alias];
  }
  return Object.values(planets).find((planet: any) => String(planet?.name || '').toLowerCase() === planetName.toLowerCase());
};

const getPlanetLongitude = (chart: any, planetName: string) => {
  const longitude = Number(getChartPlanet(chart, planetName)?.longitude);
  return Number.isFinite(longitude) ? longitude : null;
};

const buildSpecialAscendantsInput = (chart: any, panchang: any) => {
  const date = dateToYmd(chart?.birthDetails?.dateOfBirth);
  const birthClock = timeOfBirthToClock(chart?.birthDetails?.timeOfBirth);
  const sunriseClock = String(panchang?.sunrise || '').slice(0, 5);
  const sunriseDate = panchang?.date || date;
  const ascendantLongitude = Number(chart?.houses?.ascendant?.longitude ?? chart?.ascendant?.longitude);
  const required = {
    sunLongitude: getPlanetLongitude(chart, 'Sun'),
    moonLongitude: getPlanetLongitude(chart, 'Moon'),
    marsLongitude: getPlanetLongitude(chart, 'Mars'),
    jupiterLongitude: getPlanetLongitude(chart, 'Jupiter'),
    venusLongitude: getPlanetLongitude(chart, 'Venus'),
    rahuLongitude: getPlanetLongitude(chart, 'Rahu'),
  };

  if (!date || !birthClock || !/^\d{2}:\d{2}$/.test(sunriseClock) || !Number.isFinite(ascendantLongitude)) return null;
  if (Object.values(required).some((value) => value === null)) return null;

  return {
    birthDateTime: `${date}T${birthClock}`,
    sunriseDateTime: `${sunriseDate}T${sunriseClock}:00`,
    ascendantLongitude,
    ...required,
    mercuryLongitude: getPlanetLongitude(chart, 'Mercury') ?? undefined,
    saturnLongitude: getPlanetLongitude(chart, 'Saturn') ?? undefined,
    ketuLongitude: getPlanetLongitude(chart, 'Ketu') ?? undefined,
    sunLongitudeAtSunrise: Number.isFinite(Number(panchang?.details?.sunLongitude)) ? Number(panchang.details.sunLongitude) : undefined,
    tithiNumber: Number.isFinite(Number(panchang?.details?.tithiNumber)) ? Number(panchang.details.tithiNumber) : undefined,
    options: {
      bhriguBinduMode: 'average',
      varnadaDegreeMode: 'signStart',
    },
  };
};

const getHouseSignsFromAscendant = (ascendantSign: string) => {
  const startIndex = SIGNS.indexOf(ascendantSign);
  if (startIndex === -1) return null;

  return Array.from({ length: 12 }, (_, index) => {
    const sign = SIGNS[(startIndex + index) % 12];
    return { house: index + 1, sign, lord: SIGN_LORDS[sign] };
  });
};

const scoreHouseOwnership = (house: number) => {
  if (house === 5 || house === 9) return 4;
  if (house === 1) return 3;
  if (house === 4 || house === 10) return 2;
  if (house === 7) return 0;
  if (house === 2) return -1;
  if (house === 12) return -2;
  if (house === 3) return -3;
  if (house === 6) return -4;
  if (house === 8) return -5;
  if (house === 11) return -3;
  return 0;
};

const getFunctionalNatureKey = (score: number) => {
  if (score >= 5) return 'highlyShubh';
  if (score >= 2) return 'shubh';
  if (score >= 0) return 'neutral';
  if (score <= -5) return 'highlyAshubh';
  return 'ashubh';
};

const getFinalNatureKey = (functionalScore: number, placementScore = 0) => {
  const total = functionalScore + placementScore;
  if (total >= 7) return 'highlyShubh';
  if (total >= 3) return 'shubh';
  if (total >= -2) return 'mixed';
  if (total >= -6) return 'ashubh';
  return 'highlyAshubh';
};

const natureColor = (natureKey: string) => {
  if (natureKey === 'yogakaraka' || natureKey === 'highlyShubh') return '#059669';
  if (natureKey === 'shubh') return '#10B981';
  if (natureKey === 'mixed' || natureKey === 'neutral') return '#F59E0B';
  if (natureKey === 'highlyAshubh') return '#DC2626';
  if (natureKey === 'ashubh') return '#EF4444';
  return '#6B7280';
};

const isCombustPlanet = (planetKey: string, planet: any, sun: any) => {
  const orbs: Record<string, number> = { MOON: 12, MARS: 17, MERCURY: 14, JUPITER: 11, VENUS: 10, SATURN: 15 };
  const orb = orbs[planetKey];
  if (!orb || planetKey === 'SUN' || typeof planet?.longitude !== 'number' || typeof sun?.longitude !== 'number') return false;
  let arc = Math.abs(planet.longitude - sun.longitude);
  if (arc > 180) arc = 360 - arc;
  return arc < orb;
};

const getPlacementModifier = (chart: any, planetName: string) => {
  const planetKey = PLANET_NAME_TO_KEY[planetName];
  const planet = chart?.planets?.[planetKey] || chart?.planets?.[planetName];
  if (!planetKey || !planet) return { placementScore: 0, placementNotes: [] as string[] };

  const signNumber = Number(planet?.sign?.number);
  const house = Number(planet?.house || planet?.bhavaChalitHouse);
  const statusText = Array.isArray(planet?.status) ? planet.status.join(' ').toLowerCase() : String(planet?.status || '').toLowerCase();
  const notes: string[] = [];
  let score = 0;

  if (EXALT_SIGN[planetKey] === signNumber || statusText.includes('exalt')) { score += 3; notes.push('exalted'); }
  if (OWN_SIGNS[planetKey]?.includes(signNumber) || statusText.includes('own')) { score += 2; notes.push('ownSign'); }
  if (MOOLTRIKONA_SIGN[planetKey] === signNumber || statusText.includes('mool')) { score += 2; notes.push('mooltrikona'); }
  if (DEBILITATION_SIGN[planetKey] === signNumber || statusText.includes('debil')) { score -= 3; notes.push('debilitated'); }
  if (isCombustPlanet(planetKey, planet, chart?.planets?.SUN || chart?.planets?.Sun) || statusText.includes('combust')) { score -= 2; notes.push('combust'); }
  if ([6, 8, 12].includes(house)) { score -= 2; notes.push('dusthana'); }
  if ([1, 5, 9, 10].includes(house)) { score += 2; notes.push('supportiveHouse'); }
  if ([3, 6, 10, 11].includes(house)) notes.push('upachaya');

  return { placementScore: score, placementNotes: notes };
};

const calculatePlanetStates = (chart: any) => {
  const ascendantSign = chart?.houses?.ascendant?.sign?.name || chart?.ascendant?.sign?.name || chart?.ascendant?.sign || null;
  if (!ascendantSign) return null;
  const houseSigns = getHouseSignsFromAscendant(ascendantSign);
  if (!houseSigns) return null;

  const ownerships: Record<string, { house: number; sign: string }[]> = {
    Sun: [], Moon: [], Mars: [], Mercury: [], Jupiter: [], Venus: [], Saturn: [],
  };

  houseSigns.forEach((houseData) => {
    ownerships[houseData.lord]?.push({ house: houseData.house, sign: houseData.sign });
  });

  const planets = Object.entries(ownerships).map(([planet, ownedHouses]) => {
    const houses = ownedHouses.map((item) => item.house);
    const ownsTrikon = houses.some((house) => [5, 9].includes(house));
    const ownsKendra = houses.some((house) => [4, 7, 10].includes(house));
    const isYogakaraka = ownsTrikon && ownsKendra;
    const baseScore = ownedHouses.reduce((sum, item) => sum + scoreHouseOwnership(item.house), 0);
    const functionalScore = baseScore + (isYogakaraka ? 4 : 0);
    const placement = getPlacementModifier(chart, planet);
    const notes = [
      ...(isYogakaraka ? ['yogakaraka'] : []),
      ...(houses.some((house) => [2, 7].includes(house)) ? ['maraka'] : []),
      ...(houses.some((house) => [6, 8, 11].includes(house)) ? ['functionalMalefic'] : []),
      ...(houses.some((house) => [5, 9].includes(house)) ? ['trikonLord'] : []),
      ...(houses.includes(1) ? ['lagnaLord'] : []),
    ];

    return {
      planet,
      ownedHouses,
      score: functionalScore,
      placementScore: placement.placementScore,
      totalScore: functionalScore + placement.placementScore,
      naturalNatureKey: NATURAL_NATURE_KEYS[planet],
      functionalNatureKey: isYogakaraka ? 'yogakaraka' : getFunctionalNatureKey(functionalScore),
      finalNatureKey: isYogakaraka ? 'yogakaraka' : getFinalNatureKey(functionalScore, placement.placementScore),
      isYogakaraka,
      isMaraka: houses.some((house) => [2, 7].includes(house)),
      notes,
      placementNotes: placement.placementNotes,
    };
  });

  ['Rahu', 'Ketu'].forEach((planet) => {
    planets.push({
      planet,
      ownedHouses: [],
      score: null as any,
      placementScore: 0,
      totalScore: null as any,
      naturalNatureKey: NATURAL_NATURE_KEYS[planet],
      functionalNatureKey: 'depends',
      finalNatureKey: 'mixed',
      isYogakaraka: false,
      isMaraka: false,
      notes: planet === 'Rahu' ? ['nodeSignLord', 'rahuUpachaya', 'nodeConjunction'] : ['nodeSignLord', 'ketuSpiritual', 'nodeConjunction'],
      placementNotes: getPlacementModifier(chart, planet).placementNotes,
    });
  });

  return { ascendantSign, houseSigns, planets };
};

const DivisionalCharts: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { currentChart } = useChart();
  const { state: authState } = useAuth();
  const { t, i18n } = useTranslation();
  const { chartStyle, showOuterPlanets, ayanamshaMode: selectedAyanamshaMode, customAyanamsha } = useUserPreferences();
  const selectedAyanamshaKey = JSON.stringify([selectedAyanamshaMode, selectedAyanamshaMode === 'CUSTOM' ? customAyanamsha : null]);
  const shouldShowPlanet = (key: string, planet: any) => showOuterPlanets || !isOuterPlanetKey(planet?.name || key);
  const isHindi = Boolean(i18n.resolvedLanguage?.startsWith('hi') || i18n.language?.startsWith('hi'));
  const chartLanguage = i18n.resolvedLanguage || i18n.language || 'en';
  const localizedVedicNakshatra = (value: any) => {
    const index = vedicNakshatraIndex(value);
    return index < 0 ? vedicNakshatraName(value, chartLanguage) : t(`navtara.nakshatras.${index + 1}.name`, { defaultValue: vedicNakshatraName(value, chartLanguage) });
  };
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const primaryChartSize = isSmallScreen ? 300 : isMediumScreen ? 400 : 480;

  const [allCharts, setAllCharts] = useState<{ [key: string]: DivisionalChartData } | null>(null);
  const [birthDetails, setBirthDetails] = useState<any>(null);
  const [rawChartData, setRawChartData] = useState<any>(null);
  const ayanamshaMode = chartAyanamshaSettings(rawChartData || currentChart)?.mode || 'LAHIRI';
  const [shadbalData, setShadbalData] = useState<any>(null);
  const [avtData, setAvtData] = useState<any>(null);
  const [navtaraData, setNavtaraData] = useState<any>(null);
  const [navtaraAnchorPlanet, setNavtaraAnchorPlanet] = useState<string>('MOON');
  const [navtaraLoading, setNavtaraLoading] = useState(false);
  const [panchangData, setPanchangData] = useState<PanchangResult | null>(null);
  const [panchangError, setPanchangError] = useState<string | null>(null);
  const [specialPointsData, setSpecialPointsData] = useState<any | null>(null);
  const [specialPointsError, setSpecialPointsError] = useState<string | null>(null);
  const [specialPointsLoading, setSpecialPointsLoading] = useState(false);
  const [mainTab, setMainTab] = useState<number>(0);
  const [selectedDivisionalChart, setSelectedDivisionalChart] = useState<string>('D9');
  const [bavView, setBavView] = useState<string>('sav');
  const [showUpagrahas, setShowUpagrahas] = useState(false);
  const { transitChart, loading: transitLoading, error: transitError } = useCurrentTransitOverlay(rawChartData, mainTab === 3);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isHoraryChart, setIsHoraryChart] = useState(false);
  const isPremiumUser = hasPremiumSubscription(authState.user);

  // Dasha selection state
  const [selectedMahadashaKey, setSelectedMahadashaKey] = useState<string | null>(null);
  const [selectedAntardashaKey, setSelectedAntardashaKey] = useState<string | null>(null);
  const [selectedPratyantardashaKey, setSelectedPratyantardashaKey] = useState<string | null>(null);
  const [selectedSookshmaKey, setSelectedSookshmaKey] = useState<string | null>(null);
  const [selectedPranaKey, setSelectedPranaKey] = useState<string | null>(null);

  // Navtara modal state
  const [selectedNakshatra, setSelectedNakshatra] = useState<any>(null);
  const { yogas, loading: yogasLoading, error: yogasError } = useVedicYogas(rawChartData, {
    enabled: isPremiumUser,
    token: authState.token,
  });

  useEffect(() => {
    document.title = 'Divisional Charts (Varga) - Kundlistar - Nadi, KP, Vedic, Jaimini, Numerology Software';
    const controller = new AbortController();
    fetchDivisionalCharts(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line
  }, [id, currentChart, selectedAyanamshaKey, i18n.language, i18n.resolvedLanguage, isPremiumUser, authState.token]);

  const fetchNavtaraData = useCallback(async (chartId: string, language: string, signal: AbortSignal) => {
    setNavtaraLoading(true);
    try {
      const params = { language, ayanamshaMode, anchorPlanet: navtaraAnchorPlanet };
      const navtaraResp = chartId === 'preview'
        ? await axios.post(`${API_BASE_URL}/charts/preview/vedic`, { chart: currentChart }, { params })
        : await axios.get(`${API_BASE_URL}/charts/${chartId}/navtara`, { params });
      if (!signal.aborted) setNavtaraData(navtaraResp.data.data.navtara);
    } catch (_) {
      if (!signal.aborted) setNavtaraData(null);
    } finally {
      if (!signal.aborted) setNavtaraLoading(false);
    }
  }, [ayanamshaMode, currentChart, navtaraAnchorPlanet]);

  useEffect(() => {
    if (!rawChartData || isHoraryChart) return;
    const chartId = id === 'preview' ? currentChart?._id ?? id : id;
    if (!chartId || (chartId === 'preview' && !currentChart)) return;
    const language = i18n.resolvedLanguage?.startsWith('hi') || i18n.language?.startsWith('hi') ? 'hi' : 'en';
    const controller = new AbortController();
    fetchNavtaraData(chartId, language, controller.signal);
    return () => controller.abort();
  }, [currentChart, fetchNavtaraData, id, i18n.language, i18n.resolvedLanguage, isHoraryChart, rawChartData]);

  useEffect(() => {
    const loadSpecialPoints = async () => {
      if (!isPremiumUser) {
        setSpecialPointsData(null);
        setSpecialPointsError(null);
        setSpecialPointsLoading(false);
        return;
      }
      if (!rawChartData || !panchangData) {
        setSpecialPointsData(null);
        return;
      }

      let effectivePanchang = panchangData;
      if (isBirthBeforeSunrise(rawChartData, panchangData)) {
        const bd = rawChartData?.birthDetails || {};
        const place = bd.placeOfBirth || {};
        const date = dateToYmd(bd.dateOfBirth);
        const prevDate = date ? previousYmd(date) : null;
        const latitude = Number(place.latitude);
        const longitude = Number(place.longitude);
        if (prevDate && Number.isFinite(latitude) && Number.isFinite(longitude)) {
          const previousResp = await axios.get(`${API_BASE_URL}/panchang`, {
            headers: chartCalculationHeaders(rawChartData),
            params: {
              lat: latitude,
              lon: longitude,
              timezoneOffset: timezoneToMinutes(place.timezone),
              date: prevDate,
              ayanamshaMode,
            },
          });
          effectivePanchang = {
            ...previousResp.data.data.panchang,
            date: prevDate,
          };
        }
      }

      const input = buildSpecialAscendantsInput(rawChartData, effectivePanchang);
      if (!input) {
        setSpecialPointsData(null);
        setSpecialPointsError(isHindi
          ? 'जन्म समय, सूर्योदय, लग्न या ग्रहों के देशांतर उपलब्ध नहीं हैं।'
          : 'Birth time, sunrise, Lagna or planet longitudes are missing.');
        return;
      }

      try {
        setSpecialPointsLoading(true);
        setSpecialPointsError(null);
        const response = await axios.post(`${API_BASE_URL}/vedic/special-ascendants`, { input }, {
          headers: authState.token ? { Authorization: `Bearer ${authState.token}` } : undefined,
        });
        setSpecialPointsData(response.data?.data?.specialAscendants || null);
      } catch (err: any) {
        setSpecialPointsData(null);
        setSpecialPointsError(err.response?.data?.message || err.message || (isHindi
          ? 'विशेष लग्न और बिंदु की गणना नहीं हो सकी।'
          : 'Unable to calculate Special Ascendants & Points.'));
      } finally {
        setSpecialPointsLoading(false);
      }
    };

    loadSpecialPoints();
  }, [rawChartData, panchangData, isHindi, ayanamshaMode, isPremiumUser, authState.token]);

  const fetchDivisionalCharts = async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      setError(null);
      setRawChartData(null);
      setShadbalData(null);
      setAvtData(null);
      setNavtaraData(null);
      setPanchangData(null);

      const language = i18n.resolvedLanguage?.startsWith('hi') || i18n.language?.startsWith('hi') ? 'hi' : 'en';
      const requestParams = { language, calculationModule: 'vedic' };
      const requestOptions = { params: requestParams, signal };
      const isUnsavedPreview = id === 'preview' && !currentChart?._id;

      if (isUnsavedPreview) {
        if (!currentChart) {
          setError('No chart data available for preview. Please calculate a chart first.');
          return;
        }

        const response = await axios.post(
          `${API_BASE_URL}/charts/preview/vedic`,
          { chart: currentChart },
          {
            signal,
            params: { ...requestParams, anchorPlanet: navtaraAnchorPlanet },
            headers: authState.token ? { Authorization: `Bearer ${authState.token}` } : undefined,
          }
        );
        if (signal?.aborted) return;
        const data = response.data.data;
        const fullChart = data.chart;
        setRawChartData(fullChart);
        setBirthDetails(data.birthDetails || fullChart?.birthDetails || null);
        setAllCharts(data.charts);
        setShadbalData(data.shadbala || null);
        setAvtData(isPremiumUser ? data.ashtakavarga || null : null);
        setNavtaraData(data.navtara || null);
        setIsHoraryChart(fullChart?.chartType === 'horary');

        const bd = fullChart?.birthDetails || {};
        const place = bd.placeOfBirth || {};
        const date = dateToYmd(bd.dateOfBirth);
        const latitude = Number(place.latitude);
        const longitude = Number(place.longitude);
        if (date && Number.isFinite(latitude) && Number.isFinite(longitude)) {
          try {
            const panchangResp = await axios.get(`${API_BASE_URL}/panchang`, {
              headers: chartCalculationHeaders(fullChart),
              params: {
                lat: latitude,
                lon: longitude,
                timezoneOffset: timezoneToMinutes(place.timezone),
                date,
                time: timeOfBirthToClock(bd.timeOfBirth) || undefined,
                ayanamshaMode: chartAyanamshaSettings(fullChart)?.mode || 'LAHIRI',
              },
            });
            if (signal?.aborted) return;
            setPanchangData(panchangResp.data.data.panchang);
            setPanchangError(null);
          } catch (panchangErr: any) {
            if (signal?.aborted) return;
            setPanchangData(null);
            setPanchangError(panchangErr.response?.data?.detail || panchangErr.response?.data?.error || 'Failed to calculate birth Panchang.');
          }
        } else {
          setPanchangData(null);
          setPanchangError('Birth date or birthplace coordinates are missing.');
        }
        return;
      }

      const chartId = id === 'preview' ? currentChart?._id : id;
      if (!chartId) {
        setError('No chart data available for preview. Please calculate a chart first.');
        return;
      }

      const fullChartResponse = await axios.get(`${API_BASE_URL}/charts/${chartId}`, {
        ...requestOptions,
      });
      if (signal?.aborted) return;
      const fullChart = fullChartResponse.data.data.chart;
      setRawChartData(fullChart);
      setBirthDetails(fullChart?.birthDetails || null);
      try {
        const bd = fullChart?.birthDetails || {};
        const place = bd.placeOfBirth || {};
        const date = dateToYmd(bd.dateOfBirth);
        const latitude = Number(place.latitude);
        const longitude = Number(place.longitude);
        if (date && Number.isFinite(latitude) && Number.isFinite(longitude)) {
          const panchangResp = await axios.get(`${API_BASE_URL}/panchang`, {
            headers: chartCalculationHeaders(fullChart),
            params: {
              lat: latitude,
              lon: longitude,
              timezoneOffset: timezoneToMinutes(place.timezone),
              date,
              time: timeOfBirthToClock(bd.timeOfBirth) || undefined,
              ayanamshaMode: chartAyanamshaSettings(fullChart)?.mode || 'LAHIRI',
            },
          });
          if (signal?.aborted) return;
          setPanchangData(panchangResp.data.data.panchang);
          setPanchangError(null);
        } else {
          setPanchangData(null);
          setPanchangError('Birth date or birthplace coordinates are missing.');
        }
      } catch (panchangErr: any) {
        if (signal?.aborted) return;
        setPanchangData(null);
        setPanchangError(panchangErr.response?.data?.detail || panchangErr.response?.data?.error || 'Failed to calculate birth Panchang.');
      }
      if (fullChart?.chartType === 'horary') {
        setIsHoraryChart(true);
        setAllCharts({});
        setLoading(false);
        return;
      }
      setIsHoraryChart(false);

      const response = await axios.get(`${API_BASE_URL}/charts/${chartId}/divisional`, {
        ...requestOptions,
      });
      if (signal?.aborted) return;
      const { charts, birthDetails: bd } = response.data.data;
      setAllCharts(charts);
      setBirthDetails(bd);

      // Fetch full chart data and Shadbala in parallel
      try {
        const [shadResp, avtResp] = await Promise.allSettled([
          axios.get(`${API_BASE_URL}/charts/${chartId}/shadbala`, requestOptions),
          isPremiumUser
            ? axios.get(`${API_BASE_URL}/ashtakavarga/charts/${chartId}/transit`, {
                ...requestOptions,
                headers: authState.token ? { Authorization: `Bearer ${authState.token}` } : undefined,
              })
            : Promise.resolve(null),
        ]);
        if (signal?.aborted) return;
        if (shadResp.status === 'fulfilled') setShadbalData(shadResp.value.data.data.shadbala);
        if (avtResp.status === 'fulfilled' && avtResp.value) setAvtData(avtResp.value.data.data.ashtakavarga);
      } catch (_) {
        // optional sections simply won't render
      }
    } catch (err: any) {
      if (signal?.aborted) return;
      setError(err.response?.data?.message ?? 'Failed to load divisional charts');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  // ─── Computed dasha & karaka ────────────────────────────────────────────────

  const cycles = useDashaCycles(rawChartData);
  const dashaData = cycles.timeline;

  const getDashaKey = (e: any) => e ? `${e.lord}-${e.start?.toISOString?.() ?? ''}` : null;
  const normalizeKey = (value: string) => String(value || '').trim().toUpperCase();
  const slugKey = (value: string) => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  const planetName = (value: string) => t(`astrology.planets.${normalizeKey(value)}`, { defaultValue: value || '-' });
  const nakshatraName = (nak: any) => t(`navtara.nakshatras.${nak?.id}.name`, { defaultValue: nak?.name || '-' });
  const nakshatraSymbol = (nak: any) => t(`navtara.nakshatras.${nak?.id}.symbol`, { defaultValue: nak?.symbol || '-' });
  const ganaName = (value: string) => t(`navtara.gana.${value}`, { defaultValue: value || '-' });
  const taraNameText = (value: string) => t(`navtara.tara.${slugKey(value)}.name`, { defaultValue: value || '-' });
  const taraMeaningText = (value: string) => t(`navtara.tara.${slugKey(value)}.meaning`, { defaultValue: value || '-' });
  const navtaraText = (value: string) => t(`navtara.text.${slugKey(value)}`, { defaultValue: value || '-' });

  const selectedMahadasha = useMemo(() => {
    if (!dashaData) return null;
    return dashaData.mahadashas.find((e: DashaEntry) => getDashaKey(e) === selectedMahadashaKey)
      ?? dashaData.currentMahadasha ?? dashaData.mahadashas[0] ?? null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashaData, selectedMahadashaKey]);

  const selectedAntardasha = useMemo(() => {
    if (!selectedMahadasha) return null;
    return (selectedMahadasha as DashaEntry).antardashas?.find((e: DashaEntry) => getDashaKey(e) === selectedAntardashaKey)
      ?? (getDashaKey(selectedMahadasha) === getDashaKey(dashaData?.currentMahadasha) ? dashaData?.currentAntardasha : null)
      ?? (selectedMahadasha as DashaEntry).antardashas?.[0]
      ?? null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMahadasha, selectedAntardashaKey, dashaData]);

  const selectedPratyantardasha = useMemo(() => {
    if (!selectedAntardasha) return null;
    return selectedAntardasha.pratyantardashas?.find((entry: DashaEntry) => getDashaKey(entry) === selectedPratyantardashaKey)
      ?? (getDashaKey(selectedAntardasha) === getDashaKey(dashaData?.currentAntardasha) ? dashaData?.currentPratyantardasha : null)
      ?? selectedAntardasha.pratyantardashas?.[0]
      ?? null;
  }, [selectedAntardasha, selectedPratyantardashaKey, dashaData]);
  const sookshmaDashas = useMemo(() => selectedPratyantardasha ? calculateDashaSubPeriods(selectedPratyantardasha, 'Sookshma') : [], [selectedPratyantardasha]);
  const selectedSookshma = useMemo(() => sookshmaDashas.find((entry) => getDashaKey(entry) === selectedSookshmaKey)
    ?? (getDashaKey(selectedPratyantardasha) === getDashaKey(dashaData?.currentPratyantardasha) ? dashaData?.currentSookshmaDasha : null)
    ?? sookshmaDashas[0] ?? null, [sookshmaDashas, selectedSookshmaKey, selectedPratyantardasha, dashaData]);
  const pranaDashas = useMemo(() => selectedSookshma ? calculateDashaSubPeriods(selectedSookshma, 'Prana') : [], [selectedSookshma]);
  const selectedPrana = useMemo(() => pranaDashas.find((entry) => getDashaKey(entry) === selectedPranaKey)
    ?? (getDashaKey(selectedSookshma) === getDashaKey(dashaData?.currentSookshmaDasha) ? dashaData?.currentPranaDasha : null)
    ?? pranaDashas[0] ?? null, [pranaDashas, selectedPranaKey, selectedSookshma, dashaData]);

  // ─── Render helpers ─────────────────────────────────────────────────────────

  const renderStatusChips = (status: string[]) => (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {status.map(s => {
        const cfg = STATUS_CHIP_STYLES[s];
        if (!cfg) return null;
        return (
          <Chip
            key={s}
            label={s}
            size="small"
            color={cfg.color}
            title={cfg.label}
            sx={{ height: 18, fontSize: 10, fontWeight: 600 }}
          />
        );
      })}
    </Box>
  );

  const renderPlanetTable = (chartData: DivisionalChartData) => {
    const rows = Object.entries(chartData.planets).filter(([key, planet]) => PLANET_FULL[key] && shouldShowPlanet(key, planet));
    const upagrahaRows = Object.entries(chartData.upagrahas || {});
    return (
      <>
        {/* Legend */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1, mb: 0.5 }}>
          {Object.entries(STATUS_CHIP_STYLES).map(([code, cfg]) => (
            <Chip key={code} label={`${code} – ${cfg.label}`} size="small" color={cfg.color}
              sx={{ height: 18, fontSize: 10 }} />
          ))}
        </Box>
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 0.5 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t('astrology.planet')}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t('astrology.sign')}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t('panchang.fields.nakshatra')}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t('astrology.starLord')}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Pada</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>Ruler</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t('astrology.status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(([key, p]) => (
                <TableRow key={key} hover
                  sx={{
                    bgcolor:
                      p.status?.includes('E')  ? "var(--app-surface-green, #f1f8e9)" :
                      p.status?.includes('Db') ? "var(--app-surface-red, #fff8f8)" :
                      p.status?.includes('V')  ? "var(--app-surface-purple, #f3e5f5)" : 'inherit'
                  }}
                >
                  <TableCell sx={{ fontWeight: 600 }}>{getPlanetAbbr(t, key)}</TableCell>
                  <TableCell>{getSignAbbr(t, p.sign.number)}</TableCell>
                  <TableCell>{localizedVedicNakshatra(p)}</TableCell>
                  <TableCell>{vedicNakshatraLord(p) ? t(`astrology.planets.${vedicNakshatraLord(p)}`, { defaultValue: vedicNakshatraLord(p) }) : '—'}</TableCell>
                  <TableCell>{getNakshatraPada(p)}</TableCell>
                  <TableCell>{getPlanetAbbr(t, p.sign.ruler)}</TableCell>
                  <TableCell>{renderStatusChips(getPlanetPlacementStatus(key, p))}</TableCell>
                </TableRow>
              ))}
              {upagrahaRows.map(([key, p]) => (
                <TableRow key={`upagraha-${key}`} hover sx={{ bgcolor: "var(--app-surface-purple, #f3e8ff66)" }}>
                  <TableCell sx={{ fontWeight: 600, color: "var(--app-ink-secondary)" }}>
                    {vedicUpagrahaLabel(key, p.shortName, chartLanguage)}
                  </TableCell>
                  <TableCell>{getSignAbbr(t, p.sign.number)}</TableCell>
                  <TableCell>{localizedVedicNakshatra(p)}</TableCell>
                  <TableCell>{vedicNakshatraLord(p) ? t(`astrology.planets.${vedicNakshatraLord(p)}`, { defaultValue: vedicNakshatraLord(p) }) : '—'}</TableCell>
                  <TableCell>{getNakshatraPada(p)}</TableCell>
                  <TableCell>{getPlanetAbbr(t, (p.sign as any).ruler || '')}</TableCell>
                  <TableCell>
                    <Chip
                      label="Upagraha"
                      size="small"
                      color="secondary"
                      variant="outlined"
                      title={p.meaning || key}
                      sx={{ height: 18, fontSize: 10, fontWeight: 600 }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  const renderDashaSection = () => {
    if (!dashaData) return null;
    return (
      <Box sx={{ mt: 4, p: 2, bgcolor: "var(--app-surface-neutral, #faf5ff)", border: '2px solid #7b1fa2', borderRadius: 3 }}>
        <Typography variant="h6" sx={{ color: "var(--app-ink-purple, #7b1fa2)", fontWeight: 'bold', mb: 0.5 }}>
          {t('navtara.vimshottariMahadasha')}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {t('navtara.birthMdLord')}: <strong>{planetName(dashaData.birthMahadashaLord)}</strong>
          {' · '}{t('navtara.balanceAtBirth')}: <strong>{dashaData.balanceYears.toFixed(2)} {t('navtara.yearsShort')}</strong>
        </Typography>

        <DashaCycleSelector cycles={cycles} onCurrent={() => { setSelectedMahadashaKey(null); setSelectedAntardashaKey(null); setSelectedPratyantardashaKey(null); setSelectedSookshmaKey(null); setSelectedPranaKey(null); }} />
        {selectedMahadasha && selectedAntardasha && selectedPratyantardasha && selectedSookshma && selectedPrana && (
          <CollapsibleDashaTimeline
            timeline={dashaData}
            selected={{ md:selectedMahadasha, ad:selectedAntardasha, pd:selectedPratyantardasha, sookshma:selectedSookshma, prana:selectedPrana }}
            current={{ md:dashaData.currentMahadasha, ad:dashaData.currentAntardasha, pd:dashaData.currentPratyantardasha, sookshma:dashaData.currentSookshmaDasha, prana:dashaData.currentPranaDasha }}
            children={{ sookshma:sookshmaDashas, prana:pranaDashas }}
            labels={{ md:t('kp.mahadasha'), ad:t('kp.antardasha'), pd:t('kp.pratyantardasha'), sookshma:t('vedicDasha.sookshma', { defaultValue:'Sookshma Dasha' }), prana:t('vedicDasha.prana', { defaultValue:'Prana Dasha' }), current:t('common.current', { defaultValue:'Current' }) }}
            planet={(entry) => planetName(entry.lord)}
            onSelect={{
              md:(entry) => { setSelectedMahadashaKey(getDashaKey(entry)); setSelectedAntardashaKey(null); setSelectedPratyantardashaKey(null); setSelectedSookshmaKey(null); setSelectedPranaKey(null); },
              ad:(entry) => { setSelectedAntardashaKey(getDashaKey(entry)); setSelectedPratyantardashaKey(null); setSelectedSookshmaKey(null); setSelectedPranaKey(null); },
              pd:(entry) => { setSelectedPratyantardashaKey(getDashaKey(entry)); setSelectedSookshmaKey(null); setSelectedPranaKey(null); },
              sookshma:(entry) => { setSelectedSookshmaKey(getDashaKey(entry)); setSelectedPranaKey(null); },
              prana:(entry) => setSelectedPranaKey(getDashaKey(entry)),
            }}
          />
        )}
      </Box>
    );
  };

  const renderVedicRashiReference = () => {
    const signLabel = (number: number) => t(`astrology.signs.${number}`, { defaultValue: SIGNS[number - 1] });
    const planetLabel = (planetKey: string) => t(`astrology.planets.${planetKey}`, { defaultValue: PLANET_FULL[planetKey] || planetKey });
    const signNames = (numbers?: number[]) => numbers?.length ? numbers.map(signLabel).join(', ') : '—';
    return (
      <Stack spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={600} color="var(--app-ink-primary)" sx={{ mb: 1 }}>Rashi Tatva, Nature & Lord</Typography>
          <Grid container spacing={1.25}>
            {SIGNS.map((sign, index) => <Grid size={{ xs: 12, sm: 6, md: 3 }} key={sign}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: '100%' }}>
                <Typography fontWeight={600} color="var(--app-ink-primary)">{index + 1}. {signLabel(index + 1)}</Typography>
                <Typography variant="body2" fontWeight={600}>{RASHI_ELEMENTS[index % 4]}</Typography>
                <Typography variant="caption" color="text.secondary" display="block">{RASHI_MODALITIES[index % 3]}</Typography>
                <Typography variant="caption" color="text.secondary">Lord: {planetLabel(PLANET_NAME_TO_KEY[SIGN_LORDS[sign]])}</Typography>
              </Paper>
            </Grid>)}
          </Grid>
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={600} color="var(--app-ink-primary)" sx={{ mb: 1 }}>Graha–Rashi Dignity Reference</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor:'primary.main' }}>{['Graha', 'Swa Rashi', 'Mooltrikona', 'Uccha', 'Neecha'].map((label) => <TableCell key={label} sx={{ color:'#fff', fontWeight:600 }}>{label}</TableCell>)}</TableRow></TableHead>
              <TableBody>{VEDIC_PLANET_ORDER.map((planetKey) => <TableRow key={planetKey}>
                <TableCell sx={{ fontWeight:600 }}>{planetLabel(planetKey)}</TableCell>
                <TableCell>{signNames(OWN_SIGNS[planetKey])}</TableCell>
                <TableCell>{signNames(MOOLTRIKONA_SIGN[planetKey] ? [MOOLTRIKONA_SIGN[planetKey]] : [])}</TableCell>
                <TableCell sx={{ color:'success.main' }}>{signNames(EXALT_SIGN[planetKey] ? [EXALT_SIGN[planetKey]] : [])}</TableCell>
                <TableCell sx={{ color:'error.main' }}>{signNames(DEBILITATION_SIGN[planetKey] ? [DEBILITATION_SIGN[planetKey]] : [])}</TableCell>
              </TableRow>)}</TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt:0.75 }}>Rahu/Ketu Uccha and Neecha assignments vary by tradition; this table follows the convention configured in the app.</Typography>
        </Box>
      </Stack>
    );
  };

  const renderPlanetStatesSection = () => {
    const data = calculatePlanetStates(rawChartData);
    const planetLabel = (planet: string) => t(`astrology.planets.${PLANET_NAME_TO_KEY[planet] || planet.toUpperCase()}`, { defaultValue: planet });
    const natureLabel = (key: string) => t(`planetStates.nature.${key}`, { defaultValue: key });
    const noteLabel = (key: string) => t(`planetStates.notes.${key}`, { defaultValue: key });
    const placementLabel = (key: string) => t(`planetStates.placement.${key}`, { defaultValue: key });
    const joinPlanets = (items: any[]) => items.length ? items.map((item) => planetLabel(item.planet)).join(', ') : '—';

    if (!data) {
      return (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {t('planetStates.noAscendant')}
        </Alert>
      );
    }

    const shubh = data.planets.filter((item: any) => ['yogakaraka', 'highlyShubh', 'shubh'].includes(item.finalNatureKey));
    const ashubh = data.planets.filter((item: any) => ['highlyAshubh', 'ashubh'].includes(item.finalNatureKey));
    const mixed = data.planets.filter((item: any) => ['mixed', 'neutral', 'depends'].includes(item.finalNatureKey) || item.functionalNatureKey === 'depends');
    const yogakaraka = data.planets.filter((item: any) => item.isYogakaraka);
    const maraka = data.planets.filter((item: any) => item.isMaraka);

    return (
      <Box sx={{ mt: 2 }}>
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, bgcolor: 'rgba(46, 125, 82, 0.06)', mb: 2 }}>
          <Typography variant="h5" fontWeight={600} color="var(--app-ink-primary)">
            {t('planetStates.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('planetStates.subtitle', { sign: data.ascendantSign })}
          </Typography>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>{t('planetStates.summary')}</Typography>
          <Grid container spacing={1.25}>
            {[
              [t('planetStates.shubhPlanets'), joinPlanets(shubh), '#10B981'],
              [t('planetStates.ashubhPlanets'), joinPlanets(ashubh), '#EF4444'],
              [t('planetStates.mixedPlanets'), joinPlanets(mixed), '#F59E0B'],
              [t('planetStates.yogakaraka'), joinPlanets(yogakaraka), '#059669'],
              [t('planetStates.marakaPlanets'), joinPlanets(maraka), '#F59E0B'],
            ].map(([label, value, color]) => (
              <Grid size={{ xs: 12, md: 6 }} key={String(label)}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ color: adaptiveInk(String(color)), minWidth: 150 }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>{value}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {renderVedicRashiReference()}

        <Typography variant="h6" fontWeight={600} color="var(--app-ink-primary)" sx={{ mb: 1 }}>
          {t('planetStates.houseLordships')}
        </Typography>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {data.houseSigns.map((house: any) => (
            <Grid size={{ xs: 6, sm: 4, md: 2 }} key={house.house}>
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: '100%' }}>
                <Typography variant="caption" fontWeight={600} color="var(--app-ink-primary)">
                  {t('astrology.house', { number: house.house })}
                </Typography>
                <Typography variant="body1" fontWeight={600}>{house.sign}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('planetStates.lord')}: {planetLabel(house.lord)}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Typography variant="h6" fontWeight={600} color="var(--app-ink-primary)" sx={{ mb: 1 }}>
          {t('planetStates.classification')}
        </Typography>
        <Grid container spacing={2}>
          {data.planets.map((planet: any) => (
            <Grid size={{ xs: 12, md: 6 }} key={planet.planet}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 3 }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                    <Box>
                      <Typography variant="h6" fontWeight={600}>{planetLabel(planet.planet)}</Typography>
                      <Typography variant="caption" color="text.secondary">{natureLabel(planet.naturalNatureKey)}</Typography>
                    </Box>
                    <Chip
                      label={natureLabel(planet.finalNatureKey)}
                      sx={{ bgcolor: natureColor(planet.finalNatureKey), color: '#fff', fontWeight: 600 }}
                    />
                  </Stack>

                  {!!planet.ownedHouses.length && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>{t('planetStates.owns')}:</strong>{' '}
                      {planet.ownedHouses.map((item: any) => `${t('astrology.houseShort', { number: item.house })} ${item.sign}`).join(', ')}
                    </Typography>
                  )}
                  {planet.score !== null && (
                    <Typography variant="body2" color="var(--app-ink-primary)" fontWeight={600} sx={{ mt: 1 }}>
                      {t('planetStates.score')}: {planet.score} · {t('planetStates.placementScore')}: {planet.placementScore} · {t('planetStates.total')}: {planet.totalScore}
                    </Typography>
                  )}
                  {!!planet.notes.length && (
                    <List dense disablePadding sx={{ mt: 1 }}>
                      {planet.notes.map((note: string) => (
                        <ListItem key={note} disableGutters sx={{ py: 0.15 }}>
                          <ListItemText primary={noteLabel(note)} primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
                        </ListItem>
                      ))}
                    </List>
                  )}
                  {!!planet.placementNotes.length && (
                    <List dense disablePadding sx={{ mt: 0.5 }}>
                      {planet.placementNotes.map((note: string) => (
                        <ListItem key={note} disableGutters sx={{ py: 0.15 }}>
                          <ListItemText primary={placementLabel(note)} primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Alert severity="info" sx={{ mt: 2 }}>
          {t('planetStates.disclaimer')}
        </Alert>
      </Box>
    );
  };

  // ─── Ashtakavarga renderer ──────────────────────────────────────────────────

  const renderAshtakavargaSection = () => {
    if (!avtData) return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress size={32} />
        <Typography color="text.secondary" sx={{ mt: 1 }}>Loading Ashtakavarga…</Typography>
      </Box>
    );

    const SIGN_ABBR = ['Ari','Tau','Gem','Can','Leo','Vir','Lib','Sco','Sag','Cap','Aqu','Pis'];
    const PLANET_OPTIONS = [
      { value: 'sav',     label: 'Sarvashtakavarga (Total)' },
      { value: 'savRed',  label: 'SAV Reduced' },
      { value: 'sun',     label: 'Sun BAV' },
      { value: 'moon',    label: 'Moon BAV' },
      { value: 'mars',    label: 'Mars BAV' },
      { value: 'mercury', label: 'Mercury BAV' },
      { value: 'jupiter', label: 'Jupiter BAV' },
      { value: 'venus',   label: 'Venus BAV' },
      { value: 'saturn',  label: 'Saturn BAV' },
    ];

    const getScores = (): number[] => {
      if (bavView === 'sav')    return avtData.sav;
      if (bavView === 'savRed') return avtData.savReduced;
      return avtData.bav[bavView] ?? new Array(12).fill(0);
    };

    const scores   = getScores();
    const summary = avtData.predictions?.summary || {};
    const housePredictions = avtData.predictions?.housePredictions || [];
    const transit = avtData.transit || [];
    const isSav    = bavView === 'sav' || bavView === 'savRed';
    const maxScore = isSav ? 56 : 8;
    const neutral  = isSav ? 28 : 4;

    // Planet sign positions for overlay
    const positions: Record<string, number> = avtData.positions ?? {};
    const PLANET_KEY_MAP: Record<string, string> = {
      sun: 'Su', moon: 'Mo', mars: 'Ma', mercury: 'Me',
      jupiter: 'Ju', venus: 'Ve', saturn: 'Sa', lagna: 'Lg',
    };

    const planetsInSign: Record<number, string[]> = {};
    for (const [key, sIdx] of Object.entries(positions)) {
      const idx = sIdx as number;
      if (!planetsInSign[idx]) planetsInSign[idx] = [];
      planetsInSign[idx].push(t(`astrology.planetAbbr.${String(key).toUpperCase()}`, { defaultValue: PLANET_KEY_MAP[key] ?? key }));
    }

    // Bar colour by score vs neutral
    const barColor = (score: number) => {
      if (score >= neutral + 4) return '#2e7d32';
      if (score >= neutral)     return '#66bb6a';
      if (score >= neutral - 4) return '#ffa726';
      return '#ef5350';
    };
    const gradeLabel = (grade: string) => t(`ashtakavarga.grades.${grade}`, { defaultValue: grade });
    const planetLabelForAv = (planet: string) => t(`astrology.planets.${PLANET_NAME_TO_KEY[planet] || String(planet).toUpperCase()}`, { defaultValue: planet });
    const signLabelForAv = (item: any) => {
      const signNumber = typeof item.sign === 'number' ? item.sign + 1 : 0;
      return signNumber > 0 ? t(`astrology.signs.${signNumber}`, { defaultValue: item.signName }) : item.signName;
    };
    const optionLabel = (value: string, fallback?: string) => t(`ashtakavarga.options.${value}`, { defaultValue: fallback || value });

    return (
      <Box sx={{ mt: 2 }}>

        {/* Selector */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel>{t('ashtakavarga.selectBavSav')}</InputLabel>
            <Select value={bavView} label={t('ashtakavarga.selectBavSav')} onChange={e => setBavView(e.target.value)}>
              {PLANET_OPTIONS.map(o => (
                <MenuItem key={o.value} value={o.value}>{optionLabel(o.value, o.label)}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: isSav ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) minmax(0, 1.25fr)' }, gap: 2, mb: 3, alignItems: 'start' }}>
          <Box sx={{ minWidth: 0 }}>
        {/* Table summary */}
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>{t('ashtakavarga.detailedBinduTable')}</Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" sx={{ '& th, & td': { px: 0.75, py: 0.75, fontSize: 12 } }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "var(--app-surface-orange, #fffde7)" }}>
                <TableCell sx={{ fontWeight: 600 }}>{t('astrology.sign')}</TableCell>
                {PLANET_OPTIONS.filter(o => !o.value.startsWith('sav')).map(o => (
                  <TableCell key={o.value} sx={{ fontWeight: 600, fontSize: 10 }}>
                    {optionLabel(o.value, o.label).replace(` ${t('ashtakavarga.bav')}`, '')}
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 600 }}>{t('ashtakavarga.sav')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('ashtakavarga.savReduced')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {SIGN_ABBR.map((abbr, i) => (
                <TableRow key={i} hover sx={{ bgcolor: avtData.sav[i] >= 28 ? "var(--app-surface-green, #f1f8e9)" : "var(--app-surface-orange, #fff3e0)" }}>
                  <TableCell sx={{ fontWeight: 600 }}>{t(`astrology.signAbbr.${i + 1}`, { defaultValue: abbr })}</TableCell>
                  {['sun','moon','mars','mercury','jupiter','venus','saturn'].map(key => (
                    <TableCell key={key} sx={{
                      fontWeight: bavView === key ? 700 : 400,
                      color: (avtData.bav[key]?.[i] ?? 0) >= 4 ? 'success.main' : 'error.main',
                    }}>{avtData.bav[key]?.[i] ?? 0}</TableCell>
                  ))}
                  <TableCell sx={{ fontWeight: 600, color: avtData.sav[i] >= 28 ? 'success.main' : 'error.main' }}>
                    {avtData.sav[i]}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: avtData.savReduced[i] >= 28 ? 'success.main' : 'error.main' }}>
                    {avtData.savReduced[i]}
                  </TableCell>
                </TableRow>
              ))}
              {/* Totals row */}
              <TableRow sx={{ bgcolor: "var(--app-surface-neutral, #e8f5e9)" }}>
                <TableCell sx={{ fontWeight: 600 }}>{t('ashtakavarga.total')}</TableCell>
                {['sun','moon','mars','mercury','jupiter','venus','saturn'].map(key => (
                  <TableCell key={key} sx={{ fontWeight: 600 }}>
                    {(avtData.bav[key] ?? []).reduce((s: number, v: number) => s + v, 0)}
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 600 }}>{avtData.sav.reduce((s: number, v: number) => s + v, 0)}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{avtData.savReduced.reduce((s: number, v: number) => s + v, 0)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

          </Box>
          {!isSav ? <Box sx={{ minWidth: 0 }}><BavContributionTable data={avtData} planet={bavView} /></Box> : null}
        </Box>

        {/* ── Kundli chart with bindu scores ── */}
        {(() => {
          const lagnaSign = d1?.lagna?.number ?? 1; // 1-indexed
          const signFixedHouses = Array.from({ length: 12 }, (_, index) => {
            const sign = index + 1;
            const signIdx = sign - 1;
            const score = scores[signIdx] ?? 0;
            const planetsHere = planetsInSign[signIdx] ?? [];
            return {
              id: sign,
              houseLabel: `H${((sign - lagnaSign + 12) % 12) + 1}`,
              signNumber: sign,
              signLabel: t(`astrology.signAbbr.${sign}`, { defaultValue: SIGN_ABBR[signIdx] }),
              lagnaMarker: sign === lagnaSign ? t('astrology.lagnaMarker') : undefined,
              planets: [
                { abbr: String(score), degree: null, status: '', color: barColor(score) },
                ...planetsHere.map((planet) => ({ abbr: planet, degree: null, status: '', color: '#1565C0' })),
              ],
            };
          });
          if (chartStyle === 'south' || chartStyle === 'east') {
            return (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {optionLabel(bavView, PLANET_OPTIONS.find(o => o.value === bavView)?.label)} — {t('ashtakavarga.binduPerHouse')}
                  </Typography>
                  {chartStyle === 'south' ? (
                    <SouthIndianChartRenderer houses={signFixedHouses} size={340} ascSign={lagnaSign} />
                  ) : (
                    <EastIndianChartRenderer houses={signFixedHouses} size={340} ascSign={lagnaSign} />
                  )}
                </Box>
              </Box>
            );
          }
          const HPOS = [
            { id: 1,  x: 50, y: 20 },
            { id: 2,  x: 25, y: 5  },
            { id: 3,  x: 5,  y: 20 },
            { id: 4,  x: 20, y: 50 },
            { id: 5,  x: 5,  y: 80 },
            { id: 6,  x: 25, y: 95 },
            { id: 7,  x: 50, y: 80 },
            { id: 8,  x: 75, y: 95 },
            { id: 9,  x: 95, y: 80 },
            { id: 10, x: 80, y: 50 },
            { id: 11, x: 95, y: 20 },
            { id: 12, x: 75, y: 5  },
          ];
          return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {optionLabel(bavView, PLANET_OPTIONS.find(o => o.value === bavView)?.label)} — {t('ashtakavarga.binduPerHouse')}
                </Typography>
                <svg width={340} height={340} viewBox="0 0 400 400"
                  style={{ border: '2px solid #2e7d52', borderRadius: 4 }}>
                  <g stroke="#aaa" strokeWidth="1">
                    <line x1="0"   y1="0"   x2="400" y2="400" />
                    <line x1="400" y1="0"   x2="0"   y2="400" />
                    <line x1="200" y1="0"   x2="0"   y2="200" />
                    <line x1="0"   y1="200" x2="200" y2="400" />
                    <line x1="200" y1="400" x2="400" y2="200" />
                    <line x1="400" y1="200" x2="200" y2="0"   />
                  </g>
                  {HPOS.map(h => {
                    const px = (h.x / 100) * 400;
                    const py = (h.y / 100) * 400;
                    const signIdx = (lagnaSign - 1 + h.id - 1) % 12; // 0-based
                    const score   = scores[signIdx] ?? 0;
                    const bc      = barColor(score);
                    const signLabel = SIGN_ABBR[signIdx];
                    const planetsHere = planetsInSign[signIdx] ?? [];
                    // position house number label offset from center
                    const hNumY = py + (h.y >= 50 ? -22 : 22);
                    const scoreY = py + (h.y >= 50 ? 8 : -8);
                    return (
                      <g key={h.id}>
                        {/* coloured score badge background */}
                        <rect x={px - 18} y={scoreY - 13} width={36} height={18}
                          rx="4" fill={bc} opacity="0.15" />
                        {/* house number */}
                        <text x={px - 16} y={hNumY} fill="#777" fontSize="9" textAnchor="middle">
                          {t('astrology.houseShort', { number: h.id })}
                        </text>
                        {/* sign abbr */}
                        <text x={px + 18} y={hNumY} fill="#C62828" fontSize="9" textAnchor="middle">
                          {t(`astrology.signAbbr.${signIdx + 1}`, { defaultValue: signLabel })}
                        </text>
                        {/* bindu score — large */}
                        <text x={px} y={scoreY} fill={bc} fontSize="18" fontWeight="bold" textAnchor="middle">
                          {score}
                        </text>
                        {/* planet abbreviations */}
                        {planetsHere.length > 0 && (
                          <text x={px} y={scoreY + 12} fill="#1565C0" fontSize="8" textAnchor="middle">
                            {planetsHere.join(' ')}
                          </text>
                        )}
                      </g>
                    );
                  })}
                </svg>
              </Box>
            </Box>
          );
        })()}

        {/* Sign cards */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mb: 1 }}>
            {t('ashtakavarga.neutralBenchmark')}: <strong>{neutral}</strong> · {t('ashtakavarga.favourableScores', { neutral })}
          </Typography>
          <Grid container spacing={1}>
            {scores.map((score: number, i: number) => {
              const bc = barColor(score);
              const planetsHere = planetsInSign[i] ?? [];
              return (
                <Grid size={{ xs: 4, sm: 3, md: 2 }} key={i}>
                  <Paper variant="outlined" sx={{ p: 1, textAlign: 'center', borderColor: bc, borderWidth: 2,
                    bgcolor: score >= neutral ? "var(--app-surface-green, #f1f8e9)" : "var(--app-surface-orange, #fff3e0)" }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 10 }}>
                      {t(`astrology.signAbbr.${i + 1}`, { defaultValue: SIGN_ABBR[i] })}
                    </Typography>
                    <Typography variant="h5" fontWeight={600} sx={{ color: adaptiveInk(bc), lineHeight: 1.2 }}>
                      {score}
                    </Typography>
                    {planetsHere.length > 0 && (
                      <Typography variant="caption" sx={{ fontSize: 9, color: "var(--app-ink-primary)", fontWeight: 600 }}>
                        {planetsHere.join(' ')}
                      </Typography>
                    )}
                    {/* mini progress bar */}
                    <Box sx={{ mt: 0.5, height: 4, bgcolor: "var(--app-surface-neutral, #e0e0e0)", borderRadius: 1, overflow: 'hidden' }}>
                      <Box sx={{ width: `${(score / maxScore) * 100}%`, height: '100%', bgcolor: adaptiveInk(bc), borderRadius: 1 }} />
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>

                <Typography variant="h6" sx={{ color: "var(--app-ink-green, #2e7d52)", fontWeight: 600, mb: 1 }}>
          {t('ashtakavarga.ruleSummary')}
        </Typography>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {[
            [t('ashtakavarga.summary.career'), summary.career],
            [t('ashtakavarga.summary.wealth'), summary.wealth],
            [t('ashtakavarga.summary.marriage'), summary.marriage],
            [t('ashtakavarga.summary.property'), summary.property],
            [t('ashtakavarga.summary.luck'), summary.luck],
          ].filter(([, text]) => Boolean(text)).map(([label, text]) => (
            <Grid size={{ xs: 12, md: 6 }} key={String(label)}>
              <Card variant="outlined" sx={{ height: '100%', borderColor: "var(--app-border, #c8e6c9)" }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="subtitle2" fontWeight={600}>{label}</Typography>
                  <Typography variant="body2" color="text.secondary">{String(text)}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {housePredictions.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              {t('ashtakavarga.houseStrength')}
            </Typography>
            <Grid container spacing={1}>
              {housePredictions.map((house: any) => (
                <Grid size={{ xs: 6, sm: 4, md: 3 }} key={house.house}>
                  <Paper variant="outlined" sx={{ p: 1.25, height: '100%', borderColor: "var(--app-border, #c8e6c9)" }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Typography variant="caption" fontWeight={600}>{t('astrology.house', { number: house.house })}</Typography>
                      <Chip size="small" label={gradeLabel(house.grade)} color={house.grade === 'weak' ? 'error' : house.grade === 'average' ? 'warning' : 'success'} />
                    </Stack>
                    <Typography variant="h5" fontWeight={600} color="primary">{house.bindus}</Typography>
                    <Typography variant="caption" color="text.secondary">{house.area}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {transit.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              {t('ashtakavarga.currentTransitStrength')}
            </Typography>
            <Grid container spacing={1}>
              {transit.map((item: any) => (
                <Grid size={{ xs: 6, sm: 4, md: 3 }} key={item.planet}>
                  <Paper variant="outlined" sx={{ p: 1.25, height: '100%', borderColor: "var(--app-border, #c8e6c9)" }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Typography variant="caption" fontWeight={600}>{planetLabelForAv(item.planet)}</Typography>
                      <Chip size="small" label={gradeLabel(item.grade)} color={item.grade === 'weak' ? 'error' : item.grade === 'average' ? 'warning' : 'success'} />
                    </Stack>
                    <Typography variant="body2" fontWeight={600}>{signLabelForAv(item)}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {t('ashtakavarga.bav')} {item.bavBindus} · {t('ashtakavarga.sav')} {item.savBindus}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {t('ashtakavarga.kakshya')} {planetLabelForAv(item.kakshyaLord)} · {t('planetStates.score')} {item.score}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}



        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {t('ashtakavarga.reducedNote')}
        </Typography>
      </Box>
    );
  };

  const renderShadbalaSection = () => {
    if (!shadbalData) return null;

    const PLANET_ORDER = ['SUN', 'MOON', 'MARS', 'MERCURY', 'JUPITER', 'VENUS', 'SATURN'];

    return (
      <Box sx={{ mt: 4, p: 2, bgcolor: "var(--app-surface-orange, #fffde7)", border: '2px solid #f9a825', borderRadius: 3 }}>
        <Typography variant="h6" sx={{ color: "var(--app-ink-orange, #e65100)", fontWeight: 'bold', mb: 0.5 }}>
          Shadbala — Six-fold Planetary Strength
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Total strength in Shashtiamsas (1 Rupa = 60). Planet is strong when Actual ≥ Required Rupas.
        </Typography>

        {/* Summary bar chart */}
        <Box sx={{ mb: 2 }}>
          {PLANET_ORDER.map(key => {
            const p = shadbalData[key];
            if (!p) return null;
            const pct = Math.min(p.percentageStrength, 200);
            const barColor = p.isStrong ? '#4caf50' : '#ef5350';
            return (
              <Box key={key} sx={{ mb: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                  <Typography variant="caption" fontWeight={600} sx={{ minWidth: 70 }}>{PLANET_FULL[key]}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {p.rupaValue} / {p.minRequired} Rupas ({p.percentageStrength}%)
                    {p.isStrong
                      ? <Chip label="Strong" size="small" color="success" sx={{ ml: 0.5, height: 16, fontSize: 9 }} />
                      : <Chip label="Weak" size="small" color="error" sx={{ ml: 0.5, height: 16, fontSize: 9 }} />}
                  </Typography>
                </Box>
                <Box sx={{ width: '100%', bgcolor: "var(--app-surface-neutral, #f5f5f5)", borderRadius: 1, height: 10, position: 'relative', overflow: 'hidden' }}>
                  <Box sx={{ width: `${pct / 2}%`, bgcolor: barColor, height: '100%', borderRadius: 1, transition: 'width 0.5s' }} />
                  {/* 100% marker */}
                  <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1, bgcolor: 'rgba(0,0,0,0.25)' }} />
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Detailed breakdown table */}
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" sx={{ minWidth: 600 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: "var(--app-surface-orange, #fff8e1)" }}>
                <TableCell sx={{ fontWeight: 600 }}>Planet</TableCell>
                <TableCell sx={{ fontWeight: 600 }} title="Positional Strength (Ucha+Sapta+Oja+Kendra+Drekkana)">Sthana</TableCell>
                <TableCell sx={{ fontWeight: 600 }} title="Directional Strength">Dig</TableCell>
                <TableCell sx={{ fontWeight: 600 }} title="Temporal Strength (Natho+Paksha+Tribhaga+Hora)">Kaala</TableCell>
                <TableCell sx={{ fontWeight: 600 }} title="Motional Strength">Chestha</TableCell>
                <TableCell sx={{ fontWeight: 600 }} title="Natural Strength (fixed)">Naisa.</TableCell>
                <TableCell sx={{ fontWeight: 600 }} title="Aspect Strength">Drig</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Total (Sha.)</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Rupas</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Min Req.</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {PLANET_ORDER.map(key => {
                const p = shadbalData[key];
                if (!p) return null;
                return (
                  <TableRow key={key} hover sx={{ bgcolor: p.isStrong ? "var(--app-surface-green, #f1f8e9)" : "var(--app-surface-orange, #fff3e0)" }}>
                    <TableCell sx={{ fontWeight: 600 }}>{PLANET_FULL[key]}</TableCell>
                    <TableCell>{p.sthana.total}</TableCell>
                    <TableCell>{p.dig}</TableCell>
                    <TableCell>{p.kaala.total}</TableCell>
                    <TableCell>{p.chestha}</TableCell>
                    <TableCell>{p.naisargika}</TableCell>
                    <TableCell>{p.drig}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{p.total}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: p.isStrong ? 'success.main' : 'error.main' }}>{p.rupaValue}</TableCell>
                    <TableCell>{p.minRequired}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Sthana Bala sub-breakdown */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5, color: "var(--app-ink-orange, #795548)" }}>
            Sthana Bala Breakdown
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "var(--app-surface-neutral, #efebe9)" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Planet</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} title="Distance from debilitation × 1/3">Ucha</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} title="Dignity across 7 vargas">Sapta-vargiya</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} title="Odd/Even sign & navamsa">Oja-yugma</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} title="Quadrant/Succedent/Cadent house">Kendra</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} title="1st/2nd/3rd decanate">Drekkana</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {PLANET_ORDER.map(key => {
                  const p = shadbalData[key];
                  if (!p) return null;
                  return (
                    <TableRow key={key} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{PLANET_FULL[key]}</TableCell>
                      <TableCell>{p.sthana.uchaB}</TableCell>
                      <TableCell>{p.sthana.saptaB}</TableCell>
                      <TableCell>{p.sthana.ojaB}</TableCell>
                      <TableCell>{p.sthana.kendraB}</TableCell>
                      <TableCell>{p.sthana.drekkanaB}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Kaala Bala sub-breakdown */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5, color: "var(--app-ink-orange, #795548)" }}>
            Kaala Bala Breakdown
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "var(--app-surface-neutral, #efebe9)" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Planet</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} title="Day/Night birth">Nathonnatha</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} title="Lunar phase">Paksha</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} title="1/3 of day or night period">Tribhaga</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} title="Planetary hour of birth">Hora</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {PLANET_ORDER.map(key => {
                  const p = shadbalData[key];
                  if (!p) return null;
                  return (
                    <TableRow key={key} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{PLANET_FULL[key]}</TableCell>
                      <TableCell>{p.kaala.nathoB}</TableCell>
                      <TableCell>{p.kaala.pakshaB}</TableCell>
                      <TableCell>{p.kaala.tribhagaB}</TableCell>
                      <TableCell>{p.kaala.horaB}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    );
  };

  // ─── Navtara renderer ──────────────────────────────────────────────────────

  const renderNavtaraSection = () => {
    if (!navtaraData) return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress size={32} />
        <Typography color="text.secondary" sx={{ mt: 1 }}>{t('navtara.loading')}</Typography>
      </Box>
    );

    const { janmaNakshatra, planets, anchorPlanet } = navtaraData;

    const ALL_NAKSHATRAS = [
      { id:1,name:'Ashwini',symbol:'Horse Head',rulingPlanet:'Ketu',deity:'Ashwini Kumaras',zodiacRange:"0°-13°20' Aries",gana:'Deva',gender:'Male',guna:{primary:'Rajas',secondary:'Rajas',tertiary:'Rajas'},sacredTree:'Kuchla',colors:['Blood red','Crimson','Ruby red'],direction:'West',avoidDirection:'East',bird:'Swan',dosha:'Vata',syllables:['Chu','Che','Cho','La'],animal:{type:'Horse',gender:'Male'} },
      { id:2,name:'Bharani',symbol:'Yoni',rulingPlanet:'Venus',deity:'Yama',zodiacRange:"13°20'-26°40' Aries",gana:'Manushya',gender:'Female',guna:{primary:'Rajas',secondary:'Tamas',tertiary:'Rajas'},sacredTree:'Amla',colors:['Blood red','Deep red','Crimson'],direction:'South',avoidDirection:'North',bird:'Crow',dosha:'Pitta',syllables:['Li','Lu','Le','Lo'],animal:{type:'Elephant',gender:'Female'} },
      { id:3,name:'Krittika',symbol:'Razor',rulingPlanet:'Sun',deity:'Agni',zodiacRange:"26°40' Aries-10° Taurus",gana:'Rakshasa',gender:'Female',guna:{primary:'Rajas',secondary:'Rajas',tertiary:'Tamas'},sacredTree:'Fig',colors:['White','Ivory','Flame white'],direction:'East',avoidDirection:'West',bird:'Peacock',dosha:'Pitta',syllables:['A','E','U','Ea'],animal:{type:'Goat',gender:'Female'} },
      { id:4,name:'Rohini',symbol:'Cart',rulingPlanet:'Moon',deity:'Brahma',zodiacRange:"10°-23°20' Taurus",gana:'Manushya',gender:'Female',guna:{primary:'Rajas',secondary:'Rajas',tertiary:'Rajas'},sacredTree:'Jamun',colors:['White','Pearl white','Soft cream'],direction:'North',avoidDirection:'South',bird:'Owl',dosha:'Kapha',syllables:['O','Va','Vi','Vu'],animal:{type:'Serpent',gender:'Male'} },
      { id:5,name:'Mrigashira',symbol:'Deer Head',rulingPlanet:'Mars',deity:'Soma',zodiacRange:"23°20' Taurus-6°40' Gemini",gana:'Deva',gender:'Female',guna:{primary:'Tamas',secondary:'Rajas',tertiary:'Rajas'},sacredTree:'Khair',colors:['Silver grey','Ash grey','Pale silver'],direction:'South',avoidDirection:'North',bird:'Parrot',dosha:'Vata',syllables:['Ve','Vo','Ka','Ki'],animal:{type:'Serpent',gender:'Female'} },
      { id:6,name:'Ardra',symbol:'Teardrop',rulingPlanet:'Rahu',deity:'Rudra',zodiacRange:"6°40'-20° Gemini",gana:'Manushya',gender:'Female',guna:{primary:'Tamas',secondary:'Tamas',tertiary:'Tamas'},sacredTree:'Neem',colors:['Green','Emerald green','Leaf green'],direction:'West',avoidDirection:'East',bird:'Crow',dosha:'Vata',syllables:['Ku','Gha','Ing','Cha'],animal:{type:'Dog',gender:'Female'} },
      { id:7,name:'Punarvasu',symbol:'Bow',rulingPlanet:'Jupiter',deity:'Aditi',zodiacRange:"20° Gemini-3°20' Cancer",gana:'Deva',gender:'Male',guna:{primary:'Sattva',secondary:'Rajas',tertiary:'Sattva'},sacredTree:'Bamboo',colors:['Lead grey','Slate grey','Muted grey'],direction:'East',avoidDirection:'West',bird:'Swan',dosha:'Vata',syllables:['Ke','Ko','Ha','Hi'],animal:{type:'Cat',gender:'Female'} },
      { id:8,name:'Pushya',symbol:'Flower',rulingPlanet:'Saturn',deity:'Brihaspati',zodiacRange:"3°20'-16°40' Cancer",gana:'Deva',gender:'Male',guna:{primary:'Sattva',secondary:'Tamas',tertiary:'Sattva'},sacredTree:'Peepal',colors:['Black-red','Maroon','Deep burgundy'],direction:'West',avoidDirection:'East',bird:'Crow',dosha:'Kapha',syllables:['Hu','He','Ho','Da'],animal:{type:'Goat',gender:'Male'} },
      { id:9,name:'Ashlesha',symbol:'Serpent',rulingPlanet:'Mercury',deity:'Nagas',zodiacRange:"16°40'-30° Cancer",gana:'Rakshasa',gender:'Female',guna:{primary:'Sattva',secondary:'Tamas',tertiary:'Tamas'},sacredTree:'Nag Champa',colors:['Black-red','Dark maroon','Deep burgundy'],direction:'North',avoidDirection:'South',bird:'Cat Bird',dosha:'Kapha',syllables:['Di','Du','De','Do'],animal:{type:'Cat',gender:'Male'} },
      { id:10,name:'Magha',symbol:'Throne',rulingPlanet:'Ketu',deity:'Pitris',zodiacRange:"0°-13°20' Leo",gana:'Rakshasa',gender:'Male',guna:{primary:'Tamas',secondary:'Tamas',tertiary:'Tamas'},sacredTree:'Banyan',colors:['Ivory','Cream','Smoky white'],direction:'West',avoidDirection:'East',bird:'Eagle',dosha:'Pitta',syllables:['Ma','Mi','Mu','Me'],animal:{type:'Rat',gender:'Male'} },
      { id:11,name:'Purva Phalguni',symbol:'Bed',rulingPlanet:'Venus',deity:'Bhaga',zodiacRange:"13°20'-26°40' Leo",gana:'Manushya',gender:'Female',guna:{primary:'Rajas',secondary:'Rajas',tertiary:'Rajas'},sacredTree:'Palash',colors:['Light brown','Tan','Beige'],direction:'South',avoidDirection:'North',bird:'Rat Bird',dosha:'Pitta',syllables:['Mo','Ta','Ti','Tu'],animal:{type:'Rat',gender:'Female'} },
      { id:12,name:'Uttara Phalguni',symbol:'Bed',rulingPlanet:'Sun',deity:'Aryaman',zodiacRange:"26°40' Leo-10° Virgo",gana:'Manushya',gender:'Male',guna:{primary:'Rajas',secondary:'Rajas',tertiary:'Sattva'},sacredTree:'Peepal',colors:['Bright blue','Sky blue','Royal blue'],direction:'East',avoidDirection:'West',bird:'Eagle',dosha:'Pitta',syllables:['Te','To','Pa','Pi'],animal:{type:'Cow',gender:'Male'} },
      { id:13,name:'Hasta',symbol:'Hand',rulingPlanet:'Moon',deity:'Savitar',zodiacRange:"10°-23°20' Virgo",gana:'Deva',gender:'Male',guna:{primary:'Rajas',secondary:'Sattva',tertiary:'Rajas'},sacredTree:'Jasmine',colors:['Deep green','Emerald green','Forest green'],direction:'North',avoidDirection:'South',bird:'Swan',dosha:'Vata',syllables:['Pu','Sha','Na','Tha'],animal:{type:'Buffalo',gender:'Female'} },
      { id:14,name:'Chitra',symbol:'Pearl',rulingPlanet:'Mars',deity:'Vishvakarma',zodiacRange:"23°20' Virgo-6°40' Libra",gana:'Rakshasa',gender:'Female',guna:{primary:'Tamas',secondary:'Rajas',tertiary:'Tamas'},sacredTree:'Bilva',colors:['Black','Charcoal','Jet black'],direction:'South',avoidDirection:'North',bird:'Tiger Bird',dosha:'Pitta',syllables:['Pe','Po','Ra','Ri'],animal:{type:'Tiger',gender:'Female'} },
      { id:15,name:'Swati',symbol:'Coral',rulingPlanet:'Rahu',deity:'Vayu',zodiacRange:"6°40'-20° Libra",gana:'Deva',gender:'Female',guna:{primary:'Tamas',secondary:'Tamas',tertiary:'Sattva'},sacredTree:'Arjun',colors:['Black','Charcoal','Dark grey'],direction:'West',avoidDirection:'East',bird:'Pigeon',dosha:'Vata',syllables:['Ru','Re','Ro','Ta'],animal:{type:'Buffalo',gender:'Male'} },
      { id:16,name:'Vishakha',symbol:'Archway',rulingPlanet:'Jupiter',deity:'Indra-Agni',zodiacRange:"20° Libra-3°20' Scorpio",gana:'Rakshasa',gender:'Female',guna:{primary:'Sattva',secondary:'Rajas',tertiary:'Tamas'},sacredTree:'Mango',colors:['Golden','Gold','Saffron gold'],direction:'East',avoidDirection:'West',bird:'Tiger Bird',dosha:'Pitta',syllables:['Ti','Tu','Te','To'],animal:{type:'Tiger',gender:'Male'} },
      { id:17,name:'Anuradha',symbol:'Lotus',rulingPlanet:'Saturn',deity:'Mitra',zodiacRange:"3°20'-16°40' Scorpio",gana:'Deva',gender:'Male',guna:{primary:'Tamas',secondary:'Tamas',tertiary:'Sattva'},sacredTree:'Bakula',colors:['Red-brown','Russet','Copper brown'],direction:'West',avoidDirection:'East',bird:'Deer Bird',dosha:'Kapha',syllables:['Na','Ni','Nu','Ne'],animal:{type:'Deer',gender:'Female'} },
      { id:18,name:'Jyeshtha',symbol:'Earring',rulingPlanet:'Mercury',deity:'Indra',zodiacRange:"16°40'-30° Scorpio",gana:'Rakshasa',gender:'Female',guna:{primary:'Sattva',secondary:'Tamas',tertiary:'Rajas'},sacredTree:'Neem',colors:['Cream','Ivory','Pale yellow'],direction:'North',avoidDirection:'South',bird:'Deer Bird',dosha:'Pitta',syllables:['No','Ya','Yi','Yu'],animal:{type:'Deer',gender:'Male'} },
      { id:19,name:'Mula',symbol:'Roots',rulingPlanet:'Ketu',deity:'Nirriti',zodiacRange:"0°-13°20' Sagittarius",gana:'Rakshasa',gender:'Female',guna:{primary:'Tamas',secondary:'Tamas',tertiary:'Tamas'},sacredTree:'Babul',colors:['Brownish yellow','Mustard','Ochre'],direction:'West',avoidDirection:'East',bird:'Dog Bird',dosha:'Vata',syllables:['Ye','Yo','Ba','Bi'],animal:{type:'Dog',gender:'Male'} },
      { id:20,name:'Purva Ashadha',symbol:'Fan',rulingPlanet:'Venus',deity:'Apas',zodiacRange:"13°20'-26°40' Sagittarius",gana:'Manushya',gender:'Female',guna:{primary:'Rajas',secondary:'Rajas',tertiary:'Rajas'},sacredTree:'Ashoka',colors:['Black','Charcoal','Deep black'],direction:'South',avoidDirection:'North',bird:'Monkey Bird',dosha:'Pitta',syllables:['Bu','Da','Bha','Dha'],animal:{type:'Monkey',gender:'Female'} },
      { id:21,name:'Uttara Ashadha',symbol:'Elephant Tusk',rulingPlanet:'Sun',deity:'Vishvadevas',zodiacRange:"26°40' Sagittarius-10° Capricorn",gana:'Manushya',gender:'Male',guna:{primary:'Rajas',secondary:'Rajas',tertiary:'Sattva'},sacredTree:'Jackfruit',colors:['Copper','Copper red','Burnished orange'],direction:'East',avoidDirection:'West',bird:'Mongoose Bird',dosha:'Pitta',syllables:['Bhe','Bho','Ja','Ji'],animal:{type:'Mongoose',gender:'Male'} },
      { id:22,name:'Shravana',symbol:'Ear',rulingPlanet:'Moon',deity:'Vishnu',zodiacRange:"10°-23°20' Capricorn",gana:'Deva',gender:'Male',guna:{primary:'Rajas',secondary:'Sattva',tertiary:'Rajas'},sacredTree:'Arjun',colors:['Light blue','Pale blue','Sky blue'],direction:'North',avoidDirection:'South',bird:'Monkey Bird',dosha:'Kapha',syllables:['Ju','Je','Jo','Khi'],animal:{type:'Monkey',gender:'Female'} },
      { id:23,name:'Dhanishta',symbol:'Drum',rulingPlanet:'Mars',deity:'Vasus',zodiacRange:"23°20' Capricorn-6°40' Aquarius",gana:'Rakshasa',gender:'Female',guna:{primary:'Tamas',secondary:'Rajas',tertiary:'Tamas'},sacredTree:'Shami',colors:['Silver grey','Steel grey','Pale silver'],direction:'South',avoidDirection:'North',bird:'Lion Bird',dosha:'Vata',syllables:['Ga','Gi','Gu','Ge'],animal:{type:'Lion',gender:'Female'} },
      { id:24,name:'Shatabhisha',symbol:'Circle',rulingPlanet:'Rahu',deity:'Varuna',zodiacRange:"6°40'-20° Aquarius",gana:'Rakshasa',gender:'Neutral',guna:{primary:'Tamas',secondary:'Tamas',tertiary:'Tamas'},sacredTree:'Kadamba',colors:['Blue green','Teal','Turquoise'],direction:'West',avoidDirection:'East',bird:'Horse Bird',dosha:'Vata',syllables:['Go','Sa','Si','Su'],animal:{type:'Horse',gender:'Female'} },
      { id:25,name:'Purva Bhadrapada',symbol:'Sword',rulingPlanet:'Jupiter',deity:'Aja Ekapada',zodiacRange:"20° Aquarius-3°20' Pisces",gana:'Manushya',gender:'Male',guna:{primary:'Sattva',secondary:'Rajas',tertiary:'Tamas'},sacredTree:'Neem',colors:['Silver grey','Steel grey','Pale silver'],direction:'East',avoidDirection:'West',bird:'Lion Bird',dosha:'Pitta',syllables:['Se','So','Da','Di'],animal:{type:'Lion',gender:'Male'} },
      { id:26,name:'Uttara Bhadrapada',symbol:'Twin',rulingPlanet:'Saturn',deity:'Ahir Budhnya',zodiacRange:"3°20'-16°40' Pisces",gana:'Manushya',gender:'Male',guna:{primary:'Tamas',secondary:'Tamas',tertiary:'Sattva'},sacredTree:'Mango',colors:['Purple','Violet','Lavender'],direction:'West',avoidDirection:'East',bird:'Cow Bird',dosha:'Kapha',syllables:['Du','Tha','Jha','Da'],animal:{type:'Cow',gender:'Female'} },
      { id:27,name:'Revati',symbol:'Fish',rulingPlanet:'Mercury',deity:'Pushan',zodiacRange:"16°40'-30° Pisces",gana:'Deva',gender:'Female',guna:{primary:'Sattva',secondary:'Sattva',tertiary:'Sattva'},sacredTree:'Mahua',colors:['Brown','Earthy brown','Coffee brown'],direction:'North',avoidDirection:'South',bird:'Elephant Bird',dosha:'Kapha',syllables:['De','Do','Cha','Chi'],animal:{type:'Elephant',gender:'Female'} },
    ];

    const getTaraColor = (taraName: string) => {
      const favorable = ['Sampat', 'Kshema', 'Sadhaka', 'Mitra', 'Ati Mitra'];
      const unfavorable = ['Vipat', 'Pratyari', 'Vadha'];
      const critical = ['Janma'];
      
      if (favorable.includes(taraName)) return '#2e7d32';
      if (unfavorable.includes(taraName)) return '#c62828';
      if (critical.includes(taraName)) return '#f57c00';
      return '#1976d2';
    };

    const PLANET_ORDER = ['SUN', 'MOON', 'MARS', 'MERCURY', 'JUPITER', 'VENUS', 'SATURN', 'RAHU', 'KETU'];
    const TARA_ORDER = ['Janma', 'Sampat', 'Vipat', 'Kshema', 'Pratyari', 'Sadhaka', 'Vadha', 'Mitra', 'Ati Mitra'];
    // Personalized Navtara ordering starts from the selected planet's nakshatra.
    const janmaIndex = Math.max(0, ((janmaNakshatra?.id ?? 1) - 1) % 27);
    const personalizedNakshatrasByTara = TARA_ORDER.reduce((acc, taraName, taraOffset) => {
      acc[taraName] = [0, 1, 2].map((cycle) => {
        const idx = (janmaIndex + taraOffset + cycle * 9) % 27;
        return ALL_NAKSHATRAS[idx];
      });
      return acc;
    }, {} as Record<string, any[]>);

    const resolveNakshatraDetails = (nak: any) => {
      if (!nak) return null;
      const id = nak.id ?? nak.number;
      if (id) {
        const byId = ALL_NAKSHATRAS.find((n) => n.id === id);
        if (byId) return byId;
      }
      if (nak.name) {
        const byName = ALL_NAKSHATRAS.find((n) => n.name.toLowerCase() === String(nak.name).toLowerCase());
        if (byName) return byName;
      }
      return nak;
    };

    const formatAnimal = (animal: any) => {
      if (!animal) return '-';
      if (typeof animal === 'string') return animal;
      return `${animal.gender || ''} ${animal.type || ''}`.trim() || '-';
    };

    const formatGuna = (guna: any) => {
      if (!guna) return '-';
      if (typeof guna === 'string') return guna;
      return [guna.primary, guna.secondary, guna.tertiary].filter(Boolean).join(' / ') || '-';
    };

    const formatSyllables = (syllables: any) => {
      if (!syllables || !Array.isArray(syllables) || syllables.length === 0) return '-';
      return syllables.join(', ');
    };

    const formatDirection = (nakshatra: any) => {
      if (!nakshatra?.direction) return '-';
      return `${nakshatra.direction}${nakshatra.avoidDirection ? ` (avoid ${nakshatra.avoidDirection})` : ''}`;
    };

    return (
      <Box sx={{ mt: 2 }}>
        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>{t('navtara.referencePlanet', { defaultValue: 'Reference Planet' })}</InputLabel>
            <Select
              value={navtaraAnchorPlanet}
              label={t('navtara.referencePlanet', { defaultValue: 'Reference Planet' })}
              onChange={(event) => setNavtaraAnchorPlanet(String(event.target.value))}
            >
              {PLANET_ORDER.map((planetKey) => (
                <MenuItem key={planetKey} value={planetKey}>
                  {planetName(planetKey)}
                  {planets[planetKey]?.nakshatra?.name ? ` - ${nakshatraName(planets[planetKey].nakshatra)}` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {navtaraLoading && <CircularProgress size={18} sx={{ ml: 2, verticalAlign: 'middle' }} />}
        </Paper>

        {/* Header with Janma Nakshatra */}
        <Paper elevation={2} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            {t('navtara.referenceNakshatra', { defaultValue: 'Reference Nakshatra' })}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
            {nakshatraName(janmaNakshatra)}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {t('navtara.calculatedFromPlanet', {
              defaultValue: 'Calculated from {{planet}} placement',
              planet: planetName(anchorPlanet?.key || navtaraAnchorPlanet),
            })}
          </Typography>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="caption" sx={{ display: 'block', opacity: 0.9 }}>{t('navtara.symbol')}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{nakshatraSymbol(janmaNakshatra)}</Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="caption" sx={{ display: 'block', opacity: 0.9 }}>{t('navtara.rulingPlanet')}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{planetName(janmaNakshatra.rulingPlanet)}</Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="caption" sx={{ display: 'block', opacity: 0.9 }}>{t('navtara.ganaLabel')}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{ganaName(janmaNakshatra.gana)}</Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="caption" sx={{ display: 'block', opacity: 0.9 }}>{t('navtara.deity')}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{janmaNakshatra.deity}</Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Navtara Planet Table */}
        <Paper elevation={2} sx={{ mb: 3 }}>
          <Box sx={{ p: 2, bgcolor: "var(--app-surface-purple, #f3e5f5)" }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "var(--app-ink-purple, #4a148c)" }}>{t('navtara.planetNavtaraAnalysis')}</Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "var(--app-surface-purple, #f3e5f5)" }}>
                  <TableCell sx={{ fontWeight: 600 }}>{t('astrology.planet')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('navtara.nakshatra')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('navtara.taraNumber')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('navtara.taraName')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('navtara.meaning')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('navtara.interpretation')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {PLANET_ORDER.map(key => {
                  const pData = planets[key];
                  if (!pData) return null;

                  return (
                    <TableRow key={key} hover sx={{ bgcolor: key === navtaraAnchorPlanet ? "var(--app-surface-purple, #f3e5f5)" : 'inherit' }}>
                      <TableCell sx={{ fontWeight: 600, color: key === navtaraAnchorPlanet ? "var(--app-ink-purple, #7b1fa2)" : 'inherit' }}>
                        {planetName(pData.name)}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11 }}>
                        <Box sx={{ fontWeight: 600, color: 'text.primary', cursor: 'pointer' }} onClick={() => setSelectedNakshatra(resolveNakshatraDetails(pData.nakshatra))}>
                          {nakshatraName(resolveNakshatraDetails(pData.nakshatra))}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, textAlign: 'center' }}>
                        <Chip
                          label={pData.tara.number}
                          size="small"
                          sx={{ bgcolor: adaptiveInk(getTaraColor(pData.tara.name)), color: 'white', fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: adaptiveInk(getTaraColor(pData.tara.name)) }}>
                        {taraNameText(pData.tara.name)}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                        {navtaraText(pData.tara.meaning)}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>
                        {navtaraText(pData.tara.interpretation)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Full 27 Nakshatras Grid organized by Tara */}
        <Paper elevation={2} sx={{ mb: 3 }}>
          <Box sx={{ p: 2, bgcolor: "var(--app-surface-purple, #f3e5f5)" }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "var(--app-ink-purple, #4a148c)" }}>{t('navtara.completeNavtaraChart')}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('navtara.clickNakshatra')}</Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            {TARA_ORDER.map(taraName => (
              <Box key={taraName} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ width: 24, height: 24, bgcolor: adaptiveInk(getTaraColor(taraName)), borderRadius: '50%', mr: 1 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: adaptiveInk(getTaraColor(taraName)), minWidth: 120 }}>
                    {taraNameText(taraName)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', ml: 2 }}>
                    {taraMeaningText(taraName)}
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  {(personalizedNakshatrasByTara[taraName] || []).map(nak => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={nak.id}>
                      <Paper
                        sx={{
                          p: 2,
                          cursor: 'pointer',
                          border: `2px solid ${getTaraColor(taraName)}`,
                          borderRadius: 2,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            boxShadow: `0 4px 12px ${getTaraColor(taraName)}40`,
                            transform: 'translateY(-2px)'
                          }
                        }}
                        onClick={() => setSelectedNakshatra(nak)}
                      >
                        <Typography variant="body1" sx={{ fontWeight: 600, color: adaptiveInk(getTaraColor(taraName)), mb: 0.5 }}>
                          {nak.id}. {nakshatraName(nak)}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1 }}>
                          {nakshatraSymbol(nak)}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                          <Typography variant="caption">{planetName(nak.rulingPlanet)}</Typography>
                          <Typography variant="caption">{ganaName(nak.gana)}</Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}
          </Box>
        </Paper>

        {/* Legend */}
        <Box sx={{ p: 2, bgcolor: "var(--app-surface-neutral, #fafafa)", borderRadius: 2, border: "1px solid var(--app-border, #e0e0e0)" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: "var(--app-ink-purple, #4a148c)" }}>
            {t('navtara.taraSystemGuide')}
          </Typography>
          <Grid container spacing={2}>
            {[
              { name: 'Janma', meaning: 'Birth - Critical/Challenging', color: '#f57c00' },
              { name: 'Sampat', meaning: 'Prosperity - Gains & success', color: '#2e7d32' },
              { name: 'Vipat', meaning: 'Obstacles - Challenges', color: '#c62828' },
              { name: 'Kshema', meaning: 'Security - Safe & protective', color: '#2e7d32' },
              { name: 'Pratyari', meaning: 'Enemy - Conflict & opposition', color: '#c62828' },
              { name: 'Sadhaka', meaning: 'Achievement - Progress', color: '#2e7d32' },
              { name: 'Vadha', meaning: 'Death - Harmful, extreme caution', color: '#c62828' },
              { name: 'Mitra', meaning: 'Friend - Supportive', color: '#2e7d32' },
              { name: 'Ati Mitra', meaning: 'Great Friend - Most favorable', color: '#1565c0' },
            ].map(({ name, meaning, color }) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={name}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 20, height: 20, bgcolor: color, borderRadius: '50%' }} />
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                      {taraNameText(name)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      {taraMeaningText(name)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Nakshatra Details Modal */}
        <Dialog
          open={!!selectedNakshatra}
          onClose={() => setSelectedNakshatra(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ bgcolor: "var(--app-surface-purple, #f3e5f5)", fontWeight: 600, color: "var(--app-ink-purple, #4a148c)", pr: 6 }}>
            {selectedNakshatra ? nakshatraName(selectedNakshatra) : ''}
            <IconButton
              aria-label="close"
              onClick={() => setSelectedNakshatra(null)}
              sx={{ position: 'absolute', right: 8, top: 8, color: "var(--app-ink-purple, #4a148c)" }}
            >
              <Typography sx={{ fontSize: 22, lineHeight: 1 }}>×</Typography>
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {selectedNakshatra && (
              <Box>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>{t('navtara.nakshatraNumber')}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedNakshatra.id}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>{t('navtara.symbol')}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{nakshatraSymbol(selectedNakshatra)}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>{t('navtara.rulingPlanet')}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{planetName(selectedNakshatra.rulingPlanet)}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>{t('navtara.ganaLabel')}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{ganaName(selectedNakshatra.gana)}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>{t('navtara.deity')}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedNakshatra.deity}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>{t('navtara.animal')}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatAnimal(selectedNakshatra.animal)}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>{t('navtara.gender')}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedNakshatra.gender || '-'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>{t('navtara.guna')}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatGuna(selectedNakshatra.guna)}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>{t('navtara.dosha')}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedNakshatra.dosha || '-'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>{t('navtara.sacredTree')}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedNakshatra.sacredTree || '-'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>{t('navtara.bird')}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedNakshatra.bird || '-'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>{t('navtara.colors')}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{Array.isArray(selectedNakshatra.colors) ? selectedNakshatra.colors.join(', ') : '-'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>{t('navtara.zodiacRange')}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedNakshatra.zodiacRange || '-'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
                      {t('navtara.direction', { defaultValue: 'Direction' })}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatDirection(selectedNakshatra)}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>{t('navtara.syllables')}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatSyllables(selectedNakshatra.syllables)}</Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </Box>
    );
  };

  const specialPointName = (item: any) => {
    const namesHi: Record<string, string> = {
      BL: 'भाव लग्न',
      HL: 'होरा लग्न',
      GL: 'घटी लग्न',
      VGL: 'विघटी लग्न',
      VL: 'वर्णदा लग्न',
      SL: 'श्री लग्न',
      PL: 'प्राणपद लग्न',
      IL: 'इंदु लग्न',
      BB: 'भृगु बिंदु',
      D22: '22वें द्रेष्काण का स्वामी',
      N64: '64वें नवांश का स्वामी',
      BEEJA: 'बीज स्फुट',
      KSHETRA: 'क्षेत्र स्फुट',
      YOGI: 'योगी',
      SAHA_YOGI: 'सह योगी',
      AVA_YOGI: 'अव योगी',
      DAGDHA_RASHI: 'दग्ध राशियां',
    };
    return isHindi ? (namesHi[item?.code] || item?.name || '-') : (item?.name || '-');
  };

  const specialPointFromLabel = (value: string) => {
    if (!isHindi) return value;
    return value === 'Lagna' ? 'लग्न' : value === 'Moon' ? 'चंद्र' : value;
  };

  const specialPointDegreeText = (item: any) => {
    if (item?.degreeText || item?.meta?.pointText) return item.degreeText || item.meta.pointText;
    if (item?.lords?.length && (item?.meta?.fromLagnaPoint || item?.meta?.fromMoonPoint)) {
      return [
        item.meta.fromLagnaPoint ? `${specialPointFromLabel('Lagna')}: ${item.meta.fromLagnaPoint}` : null,
        item.meta.fromMoonPoint ? `${specialPointFromLabel('Moon')}: ${item.meta.fromMoonPoint}` : null,
      ].filter(Boolean).join(', ');
    }
    return '-';
  };

  const specialPointSignText = (item: any) => {
    if (item?.sign) return item.sign;
    if (item?.signs?.length) return item.signs.join(', ');
    if (item?.lords?.length) {
      return item.lords.map((lord: any) => `${specialPointFromLabel(lord.from)}: ${lord.sign}`).join(', ');
    }
    return '-';
  };

  const specialPointLordText = (item: any) => {
    if (item?.planet || item?.signLord) return item.planet || item.signLord;
    if (item?.lords?.length) {
      return item.lords.map((lord: any) => `${specialPointFromLabel(lord.from)}: ${lord.lord}${lord.sign ? ` (${lord.sign})` : ''}`).join(', ');
    }
    return '-';
  };

  const specialPointTransitText = (item: any) => {
    if (transitLoading) return isHindi ? 'लोड हो रहा है...' : 'Loading...';
    if (transitError) return '-';

    const targetSigns = [
      item?.sign,
      ...(Array.isArray(item?.signs) ? item.signs : []),
      ...(Array.isArray(item?.lords) ? item.lords.map((lord: any) => lord.sign) : []),
    ]
      .filter(Boolean)
      .map((sign: string) => String(sign).toLowerCase());

    if (!targetSigns.length) return '-';

    const matches = Object.entries(transitChart?.planets || {})
      .map(([key, planet]: [string, any]) => {
        const longitude = Number(planet?.longitude);
        const signName = planet?.sign?.name || (Number.isFinite(longitude) ? SIGNS[Math.floor((((longitude % 360) + 360) % 360) / 30)] : null);
        if (!signName || !targetSigns.includes(String(signName).toLowerCase())) return null;
        const degree = Number.isFinite(longitude) ? (((longitude % 30) + 30) % 30).toFixed(1) : null;
        return `${planetName(PLANET_FULL[key] || planet?.name || key)}${degree ? ` ${degree}°` : ''}`;
      })
      .filter(Boolean);

    return matches.length ? matches.join(', ') : '-';
  };

  const renderSpecialPointGroup = (title: string, items: any[] = [], showTransitColumn = false) => {
    if (!items.length) return null;
    return (
      <Card sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: "var(--app-ink-primary)", fontWeight: 600, mb: 1.5 }}>
            {title}
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{isHindi ? 'वस्तु' : 'Object'}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{isHindi ? 'डिग्री' : 'Degree'}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{isHindi ? 'राशि' : 'Rashi'}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{isHindi ? 'नक्षत्र' : 'Nakshatra'}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{isHindi ? 'स्वामी' : 'Lord'}</TableCell>
                  {showTransitColumn ? (
                    <TableCell sx={{ fontWeight: 600 }} title="Current transit planets in the same sensitive rashi">
                      {isHindi ? 'गोचर ग्रह' : 'Transiting Planets'}
                    </TableCell>
                  ) : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => (
                  <TableRow key={`${item?.code || title}-${index}`} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{specialPointName(item)}</Typography>
                      <Typography variant="caption" color="text.secondary">{item?.code || '-'}</Typography>
                      {item?.meaning ? (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {item.meaning}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>{specialPointDegreeText(item)}</TableCell>
                    <TableCell>{specialPointSignText(item)}</TableCell>
                    <TableCell>{item?.nakshatra || '-'}</TableCell>
                    <TableCell>{specialPointLordText(item)}</TableCell>
                    {showTransitColumn ? <TableCell>{specialPointTransitText(item)}</TableCell> : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };

  const renderSpecialAscendantsSection = () => (
    <Box sx={{ mt: 2 }}>
      <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: "var(--app-ink-primary)", mb: 0.5 }}>
          {isHindi ? 'विशेष लग्न और बिंदु' : 'Special Ascendants & Points'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {isHindi
            ? 'सूर्योदय-आधारित पाराशरी पद्धति से विशेष लग्न, स्फुट, योगी और दग्ध राशि बिंदु।'
            : 'Sunrise-based Parasari-style special lagnas, sphutas, Yogi points and Dagdha Rashi points.'}
        </Typography>

        {specialPointsLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={22} />
            <Typography color="text.secondary">
              {isHindi ? 'विशेष बिंदु लोड हो रहे हैं...' : 'Loading special points...'}
            </Typography>
          </Box>
        ) : specialPointsError ? (
          <Alert severity="warning">{specialPointsError}</Alert>
        ) : specialPointsData ? (
          <>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              <Chip size="small" label={`${isHindi ? 'घटी' : 'Ghatis'}: ${specialPointsData.elapsedGhatis?.toFixed?.(2) || '-'}`} />
              <Chip size="small" label={`${isHindi ? 'विघटी' : 'Vighatis'}: ${specialPointsData.elapsedVighatis?.toFixed?.(2) || '-'}`} />
              <Chip size="small" label={`${isHindi ? 'तिथि' : 'Tithi'}: ${specialPointsData.tithiNumber || '-'}`} />
            </Stack>
            {renderSpecialPointGroup(isHindi ? 'विशेष लग्न' : 'Special Ascendants', specialPointsData.specialAscendants, true)}
            {renderSpecialPointGroup(isHindi ? 'संवेदनशील बिंदु' : 'Sensitive Points', specialPointsData.sensitivePoints, true)}
            {renderSpecialPointGroup(isHindi ? 'संतान बिंदु' : 'Progeny Points', specialPointsData.progenyPoints, true)}
            {renderSpecialPointGroup(isHindi ? 'भाग्य बिंदु' : 'Fortune Points', specialPointsData.fortunePoints, true)}
            {renderSpecialPointGroup(isHindi ? 'तिथि बिंदु' : 'Tithi Points', specialPointsData.tithiPoints, true)}
            <Alert severity="info" sx={{ mt: 1 }}>
              {isHindi
                ? 'वर्णदा लग्न की डिग्री परंपरा/सॉफ्टवेयर के अनुसार बदल सकती है। यहां राशि सही रखकर डिफॉल्ट में राशि-आरंभ डिग्री दिखाई गई है।'
                : 'Varnada Lagna degree can vary by tradition/software. This view keeps the correct sign and shows sign-start degree by default.'}
            </Alert>
          </>
        ) : (
          <Alert severity="info">
            {isHindi ? 'इस चार्ट के लिए विशेष बिंदु उपलब्ध नहीं हैं।' : 'Special points are not available for this chart.'}
          </Alert>
        )}
      </Paper>
    </Box>
  );

  const renderPanchangSection = () => {
    const locationLabel = [birthDetails?.placeOfBirth?.city, birthDetails?.placeOfBirth?.country].filter(Boolean).join(', ') || 'Birthplace';

    return (
      <Box sx={{ mt: 2 }}>
        <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: "var(--app-ink-primary)", mb: 0.5 }}>
            {t('panchang.birthTitle', { defaultValue: 'Birth Panchang' })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('panchang.birthDescription', {
              defaultValue: 'Calculated for the person’s saved birth time and birthplace.',
            })}
          </Typography>

          {panchangError ? (
            <Alert severity="warning">{panchangError}</Alert>
          ) : !panchangData ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={22} />
              <Typography color="text.secondary">Loading Panchang…</Typography>
            </Box>
          ) : (
            <PanchangAlmanac panchang={panchangData} locationLabel={locationLabel} compact />
          )}
        </Paper>
      </Box>
    );
  };

  const renderPlanPaywall = (title: string, message: string) => (
    <Paper sx={{ p: 3, textAlign: 'center' }}>
      <Typography variant="h6" sx={{ fontWeight: 600, color: "var(--app-ink-primary)" }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>{message}</Typography>
      <Button variant="contained" onClick={() => window.location.assign(authState.user ? '/pricing' : '/login')}>
        {authState.user ? 'Choose Plan' : 'Login to Upgrade'}
      </Button>
    </Paper>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', flexDirection: 'column', gap: 2 }}>
          <CircularProgress size={48} />
          <Typography color="text.secondary">Calculating divisional charts…</Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!allCharts) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning">No chart data found.</Alert>
      </Container>
    );
  }

  if (isHoraryChart) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, bgcolor: "var(--app-surface-neutral, #fafafa)", minHeight: '100vh' }}>
        {id && id !== 'preview' && <AnalysisTabBar chartId={id} active="vedic" isHoraryChart={true} />}
        <Alert severity="info">Not available for horary chart.</Alert>
      </Container>
    );
  }

  const d1 = allCharts['D1'];
  const d1WithUpagrahas = d1 ? { ...d1, upagrahas: rawChartData?.upagrahas || d1.upagrahas } : d1;
  const divisionalChartOptions = DIVISIONAL_CHART_ORDER.filter((key) => Boolean(allCharts[key]));
  const selectedDivisionalData = selectedDivisionalChart === 'D1'
    ? d1WithUpagrahas
    : allCharts[selectedDivisionalChart] ?? null;
  const moon = d1?.planets?.MOON;
  const chandraChart = moon?.sign ? {
    ...d1WithUpagrahas,
    chartKey: 'CHANDRA',
    chartName: 'Chandra Kundli',
    purpose: 'Moon-sign chart for mind, emotions and lived experience',
    lagna: {
      number: moon.sign.number,
      name: moon.sign.name,
      ruler: moon.sign.ruler,
    },
  } : null;
  const hasUpagrahas = Object.keys(d1WithUpagrahas?.upagrahas || {}).length > 0;
  return (
    <Container maxWidth="xl" sx={{ py: 4, bgcolor: "var(--app-surface-neutral, #fafafa)", minHeight: '100vh' }}>
      <SeoHead
        title={`${DIVISIONAL_TAB_META[mainTab]?.title || 'Divisional Charts'} | Kundlistar`}
        description={DIVISIONAL_TAB_META[mainTab]?.description || 'Divisional chart analysis for this chart.'}
        path={id ? `/chart/${id}/divisional` : '/chart/preview/divisional'}
        robots="noindex,nofollow"
        keywords="divisional charts, ashtakavarga, shadbala, vimshottari dasha, navtara, Kundlistar"
      />

      {/* Navigation tabs */}
      {id && id !== 'preview' && <AnalysisTabBar chartId={id} active="vedic" isHoraryChart={isHoraryChart} />}

      {/* Header */}
      <Box sx={{ mb: 3, bgcolor: 'primary.main', p: 3, borderRadius: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
        >
          <Box sx={{ textAlign: { xs: 'center', md: 'left' }, flex: 1 }}>
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
              {birthDetails?.name ?? 'Divisional Charts'}
            </Typography>
            {birthDetails && (
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mt: 0.5 }}>
                Vedic Analysis
              </Typography>
            )}
          </Box>
        </Stack>
      </Box>

      {/* ── Sub-tab navigation ── */}
      <Paper elevation={0} sx={{ mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        <Tabs
          value={mainTab}
          onChange={(_, v) => setMainTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab value={0} label={t('vedicTabs.charts', { defaultValue: 'Charts' })} />
          <Tab value={14} label={t('vedicTabs.divisionalCharts')} />
          <Tab value={5} label={t('vedicTabs.mahadasha')} />
          <Tab value={12} label={dashaAnalysisLabel(i18n.resolvedLanguage || i18n.language)} />
          <Tab value={1} label={t('vedicTabs.planetStates')} />
          <Tab value={13} label={t('nav.faladesh')} />
          <Tab value={2} label={t('vedicTabs.ashtakavarga')} />
          <Tab value={9} label={t('doubleTransit.title')} />
          <Tab value={3} label={t('vedicTabs.specialAscendants')} />
          <Tab value={4} label={t('vedicTabs.shadbala')} />
          <Tab value={11} label={sadeSatiText(i18n.resolvedLanguage || i18n.language, 'title')} />
          <Tab value={6} label={t('vedicTabs.navtara')} />
          <Tab value={7} label={t('vedicTabs.panchang')} />
          <Tab value={8} label={t('vedicTabs.yogas')} />
        </Tabs>
      </Paper>

      {/* ── Tab 0: Natal charts ── */}
      {mainTab === 0 && (
        <>
          {hasUpagrahas && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 4 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showUpagrahas}
                    onChange={(event) => setShowUpagrahas(event.target.checked)}
                    color="secondary"
                  />
                }
                label="Show Upagrahas"
                sx={{ m: 0 }}
              />
            </Box>
          )}

          <Grid container spacing={4} justifyContent="center">
            <Grid size={{ xs: 12 }}>
              <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
                {rawChartData?.houses?.ascendant && (
                  <Box sx={{ mb: 2, p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'primary.light', bgcolor: 'action.hover' }}>
                    <Typography variant="subtitle2" fontWeight={600} color="var(--app-ink-primary)">{t('astrology.ascendant')}</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {t(`astrology.signs.${rawChartData.houses.ascendant.sign?.number}`, { defaultValue: rawChartData.houses.ascendant.sign?.name || '-' })} · {localizedVedicNakshatra(rawChartData.houses.ascendant)} · {translateInline(chartLanguage, 'Pada')} {getNakshatraPada(rawChartData.houses.ascendant)}
                      {rawChartData.houses.ascendant.degree?.formatted ? ` · ${rawChartData.houses.ascendant.degree.formatted}` : ''}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 3, alignItems: 'start' }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <DivisionalChart
                        chartData={d1WithUpagrahas}
                        size={primaryChartSize}
                        showUpagrahas={showUpagrahas}
                      />
                    </Box>
                  </Box>
                  {chandraChart && (
                    <Box sx={{ minWidth: 0, display: 'flex', justifyContent: 'center' }}>
                      <DivisionalChart chartData={chandraChart} size={primaryChartSize} showUpagrahas={showUpagrahas} />
                    </Box>
                  )}
                  <Box sx={{ minWidth: 0, display: 'flex', justifyContent: 'center' }}>
                    <BhavaChalitChart chartData={rawChartData || currentChart} size={primaryChartSize} showRotationControl vedicPlacement />
                  </Box>
                </Box>
                {renderPlanetTable(d1WithUpagrahas)}
              </Paper>
            </Grid>

          </Grid>
        </>
      )}

      {/* ── Tab 14: Selectable divisional charts ── */}
      {mainTab === 14 && (
        <Paper elevation={2} sx={{ p: { xs: 1.5, md: 3 }, borderRadius: 2 }}>
          <FormControl fullWidth size="small" sx={{ maxWidth: 420, mb: 2.5 }}>
            <InputLabel>{t('vedicTabs.divisionalCharts')}</InputLabel>
            <Select
              value={selectedDivisionalChart}
              label={t('vedicTabs.divisionalCharts')}
              onChange={(event) => setSelectedDivisionalChart(String(event.target.value))}
            >
              {divisionalChartOptions.map((key) => (
                <MenuItem key={key} value={key}>
                  {allCharts[key]?.chartName || key}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedDivisionalData ? (
            <Stack spacing={2.5}>
              <Box sx={{ display: 'flex', justifyContent: 'center', minWidth: 0 }}>
                <DivisionalChart
                  chartData={selectedDivisionalData}
                  size={primaryChartSize}
                  showUpagrahas={selectedDivisionalChart === 'D1'}
                />
              </Box>
              {renderPlanetTable(selectedDivisionalData)}
            </Stack>
          ) : <Alert severity="warning">{t('common.noData', { defaultValue: 'No chart data found.' })}</Alert>}
        </Paper>
      )}

      {/* ── Tab 1: Planet States ── */}
      {mainTab === 1 && renderPlanetStatesSection()}

      {/* ── Tab 8: Yogas ── */}
      {mainTab === 8 && (
        isPremiumUser ? (
          <VedicYogasPanel yogas={yogas} loading={yogasLoading} error={yogasError} />
        ) : (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "var(--app-ink-primary)" }}>
              Plan Feature
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              Vedic Yogas are available for plan users.
            </Typography>
            <Button variant="contained" onClick={() => window.location.assign(authState.user ? '/pricing' : '/login')}>
              {authState.user ? 'Choose Plan' : 'Login to Upgrade'}
            </Button>
          </Paper>
        )
      )}

      {mainTab === 9 && (
        isPremiumUser ? (
          <DoubleTransitPanel
            chartId={id && id !== 'preview' ? id : null}
            chartData={rawChartData || currentChart}
            token={authState.token}
          />
        ) : (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "var(--app-ink-primary)" }}>{t('doubleTransit.premiumTitle')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>{t('doubleTransit.premiumMessage')}</Typography>
            <Button variant="contained" onClick={() => window.location.assign(authState.user ? '/pricing' : '/login')}>
              {authState.user ? t('common.upgrade') : t('doubleTransit.loginToUpgrade')}
            </Button>
          </Paper>
        )
      )}

      {/* ── Tab 2: Ashtakavarga ── */}
      {mainTab === 2 && (isPremiumUser
        ? renderAshtakavargaSection()
        : renderPlanPaywall('Plan Ashtakavarga', 'Ashtakavarga bindu scores, house strengths, and transit analysis are available for plan users.'))}

      {/* ── Tab 3: Special Ascendants & Points ── */}
      {mainTab === 3 && (isPremiumUser
        ? renderSpecialAscendantsSection()
        : renderPlanPaywall('Plan Special Ascendants & Points', 'Special ascendants and sensitive Vedic points are available for plan users.'))}

      {/* ── Tab 4: Shadbala ── */}
      {mainTab === 4 && renderShadbalaSection()}

      {/* ── Tab 11: Sade Sati & Dhaiya ── */}
      {mainTab === 11 && (isPremiumUser
        ? <SadeSatiPanel key={selectedAyanamshaKey} apiBaseUrl={API_BASE_URL} chartId={id === 'preview' ? currentChart?._id : id} ayanamshaMode={ayanamshaMode} token={authState.token} />
        : renderPlanPaywall('Plan Sade Sati & Dhaiya', 'Sade Sati cycles and fourth/eighth-house Dhaiya analysis are available for plan users.'))}

      {/* ── Tab 5: Mahadasha ── */}
      {mainTab === 5 && renderDashaSection()}

      {mainTab === 12 && <DashaAnalysisPanel chart={rawChartData || currentChart} system="VEDIC" />}
      {mainTab === 13 && <FaladeshSystemPanel chartId={id === 'preview' ? currentChart?._id : id} system="VEDIC" />}

      {/* ── Tab 6: Navtara ── */}
      {mainTab === 6 && renderNavtaraSection()}

      {/* ── Tab 7: Panchang ── */}
      {mainTab === 7 && renderPanchangSection()}
    </Container>
  );
};

export default DivisionalCharts;
